/**
 * SEC-AI Frontend - Social Engineering Consultant AI
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import EngagementBar from './components/EngagementBar';
import KnowledgePanel from './components/KnowledgePanel';
import PretextStudio from './components/PretextStudio';
import GlossaryPanel from './components/GlossaryPanel';
import PromptLibraryPanel from './components/PromptLibraryPanel';
import ReportReviewPanel from './components/ReportReviewPanel';
import ClientSupportForm from './components/ClientSupportForm';
import PdfPasswordPrompt from './components/PdfPasswordPrompt';
import WorkspaceSidebar from './components/WorkspaceSidebar';
import ActivityBanner from './components/ActivityBanner';
import HostAlertBanner from './components/HostAlertBanner';
import ElapsedTimer from './components/ElapsedTimer';
import AssistantFollowUp from './components/AssistantFollowUp';
import CitationsPanel from './components/CitationsPanel';
import NationStateConsent from './components/NationStateConsent';
import LoginGate from './components/LoginGate';
import SplashScreen from './components/SplashScreen';
import AdminPortal from './components/AdminPortal';
import SignupRequestPage from './components/SignupRequestPage';
import SetupAccountPage from './components/SetupAccountPage';
import ReportBuilderPanel from './components/ReportBuilderPanel';
import Logo from './components/Logo';
import SimplifiedTier1App from './components/SimplifiedTier1App';
import { formatChatMessageForDisplay } from './utils/markdownDisplay';
import {
  mergeTier1ReportChatIntoConversation,
  tier1ReportChatMessagesFromConversation,
} from './utils/tier1ReportChatMessages';
import {
  API_BASE_URL,
  CONSULTANT_MODES,
  MODE_SPECIALTIES,
  DEFAULT_CONSULTANT_MODE,
  getDefaultSpecialtyForMode,
  resolveSpecialtyForMode,
  SE_BRAND,
  getProductBrandForMode,
  getAllowedModes,
  getDefaultModeForProfile,
  isModeAllowedForProfile,
  REPORT_REVIEW_MODES,
  WORKSPACE_PANELS,
  WORKSPACE_PANELS_STORAGE_KEY,
  loadWorkspacePanels,
  getDefaultWorkspacePanels,
  LANGUAGE_OPTIONS,
  getLanguageLabel,
  getWelcomeMessage,
  apiFetch,
  parseJsonResponse,
  consumeChatStream,
  waitForChatJobCompletion,
  isDegenerateAssistantContent,
  formatApiErrorDetail,
  createSessionFromApi,
  getApiHeaders,
  getStoredAuthProfile,
  getStoredAuthToken,
  clearAuth,
  storeAuth,
  hasLegacyApiKeyAuth,
  AUTH_PROFILE_KEY,
  getStoredActiveSession,
  setStoredActiveSession,
  normalizeAuthProfile,
  canSwitchProductMode,
  isSuperadminRole,
  isOrgAdminRole,
  canWriteConfig,
  canWritePromptLibrary,
  canViewPromptLibraryContent,
  canFeedLlm,
  canUseChat,
  canSyncKb,
  canManageKbNoindex,
  getFeedFileAccept,
  formatUploadErrorMessage,
  getStoredAdminView,
  setStoredAdminView,
  clearStoredAdminView,
} from './constants';
import { tUi } from './utils/uiText';
import { resolveAppliedSessionChatText, resolveEffectiveChatDraft } from './utils/promptLibraryApply';
import { validateReportTranslation } from './utils/reportTranslation';
import {
  filterReportByExcludedSections,
  hasExportableReportContent,
  stripAuthorizationFromReportData,
} from './utils/reportSections';
import { applyReportExclusions } from './utils/reportPretext';
import { buildExportFilename, parseContentDispositionFilename } from './utils/exportFilename';
import {
  buildReportProgressSteps,
  formatReportProgressStatus,
} from './utils/reportProgress';

const PREFERRED_EXPORT_FORMAT_KEY = 'sec_ai_preferred_export_format'; // TODO: move to backend profile persistence when available.

const isUploadAckMessage = (content) =>
  /^I've received (your file|\d+ files)/i.test((content || '').trim());

const resolveLanguageValue = (selectValue, customValue) => {
  if (selectValue === '__custom__') {
    return customValue.trim() || 'auto';
  }
  return selectValue || 'auto';
};

const EXERCISE_CONSULTANT_MODES = new Set([
  'nation_state',
]);

const CLASSIFICATION_PRESET_VALUES = new Set(['UNCLASSIFIED', 'OFFICIAL', 'CONFIDENTIAL', 'SECRET']);

const hydrateClassificationSelect = (storedClassification, storedBanner) => {
  const preset = (storedClassification || '').trim();
  if (preset && CLASSIFICATION_PRESET_VALUES.has(preset)) {
    return { select: preset, custom: '', banner: storedBanner || preset };
  }
  const banner = (storedBanner || '').trim();
  if (banner && !CLASSIFICATION_PRESET_VALUES.has(banner)) {
    return { select: '__custom__', custom: banner, banner };
  }
  return { select: preset || 'UNCLASSIFIED', custom: '', banner: banner || 'FOR AUTHORIZED USE ONLY' };
};

const hydrateLanguageSelect = (storedValue) => {
  if (!storedValue || storedValue === 'auto') {
    return { select: 'auto', custom: '' };
  }
  const known = LANGUAGE_OPTIONS.some((item) => item.code === storedValue);
  if (known) {
    return { select: storedValue, custom: '' };
  }
  return { select: '__custom__', custom: storedValue };
};

const pickTranslationTarget = (reportLanguage, customReportLanguage, chatLanguage, customChatLanguage) => {
  const reportValue = resolveLanguageValue(reportLanguage, customReportLanguage);
  if (reportValue !== 'auto') return reportValue;
  const chatValue = resolveLanguageValue(chatLanguage, customChatLanguage);
  if (chatValue !== 'auto') return chatValue;
  return null;
};

const activeUserLanguage = (chatLanguage, customChatLanguage) =>
  resolveLanguageValue(chatLanguage, customChatLanguage);

const activeClientLanguage = (reportLanguage, customReportLanguage) =>
  resolveLanguageValue(reportLanguage, customReportLanguage);

const resolveUiLanguage = (chatLanguage, customChatLanguage) => {
  const language = activeUserLanguage(chatLanguage, customChatLanguage);
  if (!language || language === 'auto') return 'en';
  return language;
};

const resolveReportSourceLanguage = (reportLanguage, customReportLanguage, analystLanguage, customAnalystLanguage) => {
  const reportValue = resolveLanguageValue(reportLanguage, customReportLanguage);
  if (reportValue && reportValue !== 'auto') {
    return reportValue;
  }
  const analystValue = resolveLanguageValue(analystLanguage, customAnalystLanguage);
  if (analystValue && analystValue !== 'auto') {
    return analystValue;
  }
  return 'en';
};

const getChatPlaceholder = (chatLanguage, customChatLanguage, speechSupported) => {
  const language = activeUserLanguage(chatLanguage, customChatLanguage);
  const voiceHint = speechSupported ? ' or click the microphone' : '';
  if (!language || language === 'auto') {
    return `Type in any language${voiceHint} — the consultant will understand and reply in your language...`;
  }
  return `Type in ${getLanguageLabel(language)}${voiceHint}...`;
};

const formatHistoryTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const generateAutoTitle = (currentClientName, messagesSnapshot) => {
  const now = new Date();
  const day = now.getDate();
  const month = now.toLocaleString('en-GB', { month: 'long' });
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  let target = (currentClientName && currentClientName !== 'Client') ? currentClientName.trim() : '';
  if (!target && Array.isArray(messagesSnapshot)) {
    const firstUser = messagesSnapshot.find((m) => m.role === 'user');
    if (firstUser && firstUser.content) {
      const match = firstUser.content.match(/\b([A-Z][a-zA-Z]{2,})\b/);
      if (match) target = match[1];
    }
  }
  if (!target) target = 'Consultation';
  return `${day} ${month} ${year} ${hours}${minutes} ${target}`;
};

// Service Inquiry Form Component
function ServiceInquiryForm({ onSubmit, onCancel, loading }) {
  const [email, setEmail] = useState('');
  const [wantsServices, setWantsServices] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      onSubmit(email.trim(), wantsServices);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Email Address *
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-2 bg-black border border-teal-500/30 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-white placeholder-gray-400"
          placeholder="your.email@example.com"
          disabled={loading}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          I'm interested in:
        </label>
        <div className="space-y-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              checked={wantsServices}
              onChange={() => setWantsServices(true)}
              className="text-teal-500 focus:ring-teal-500"
              disabled={loading}
            />
            <span className="text-gray-300">Services or Training</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              checked={!wantsServices}
              onChange={() => setWantsServices(false)}
              className="text-teal-500 focus:ring-teal-500"
              disabled={loading}
            />
            <span className="text-gray-300">Just have questions</span>
          </label>
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="px-6 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all duration-200"
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function tier1DraftKey(draft) {
  if (!draft) return '';
  return JSON.stringify({
    report_name: draft.report_name || '',
    pretext_limit: draft.pretext_limit || '',
    mission_threat_assessment: Boolean(draft.mission_threat_assessment),
    mission_fugitive_location: Boolean(draft.mission_fugitive_location),
    investigator_notes: draft.investigator_notes || '',
  });
}

function normalizeTier1Draft(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    report_name: String(raw.report_name || ''),
    pretext_limit: String(raw.pretext_limit || ''),
    mission_threat_assessment: Boolean(raw.mission_threat_assessment),
    mission_fugitive_location: Boolean(raw.mission_fugitive_location),
    investigator_notes: String(raw.investigator_notes || ''),
  };
}

function ConsultantApp({ authProfile, onLogout }) {
  const allowedModes = useMemo(() => getAllowedModes(authProfile), [authProfile]);
  const entitledModeOptions = useMemo(
    () => Object.values(CONSULTANT_MODES).filter((mode) => allowedModes.includes(mode.id)),
    [allowedModes],
  );
  const [showAdminPortal, setShowAdminPortal] = useState(() => {
    const stored = getStoredAdminView();
    return Boolean(stored.open) && isSuperadminRole(getStoredAuthProfile()?.role);
  });
  const [showClientSupport, setShowClientSupport] = useState(false);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [hostStatus, setHostStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const pollHostStatus = async () => {
      try {
        const response = await apiFetch('/api/system/host-status');
        if (!response.ok) return;
        const data = await parseJsonResponse(response);
        if (!cancelled) setHostStatus(data);
      } catch {
        // Non-fatal — banner simply stays hidden until next poll.
      }
    };

    pollHostStatus();
    const timer = window.setInterval(pollHostStatus, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const hostRebootAlert = useMemo(() => {
    if (!hostStatus?.reboot_required) return null;
    return {
      title: 'REBOOT IS NEEDED',
      detail: hostStatus.reboot_reason
        || 'Schedule a maintenance reboot before GPU/Ollama failures affect reports and chat.',
    };
  }, [hostStatus]);

  const hostActivityWarnings = useMemo(() => {
    if (!hostRebootAlert) return [];
    return [hostRebootAlert.title];
  }, [hostRebootAlert]);

  useEffect(() => {
    const stored = getStoredAdminView();
    if (stored.open && isSuperadminRole(authProfile?.role)) {
      setShowAdminPortal(true);
    }
  }, [authProfile?.role]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatJobStartedAt, setChatJobStartedAt] = useState(null);
  const [reportStartedAt, setReportStartedAt] = useState(null);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const sessionIdRef = useRef('');
  const [consultantMode, setConsultantMode] = useState(() => getDefaultModeForProfile(authProfile));
  const consultantModeRef = React.useRef(getDefaultModeForProfile(authProfile));
  const [consultantSpecialty, setConsultantSpecialty] = useState(
    getDefaultSpecialtyForMode(getDefaultModeForProfile(authProfile)),
  );
  const consultantSpecialtyRef = React.useRef(
    getDefaultSpecialtyForMode(getDefaultModeForProfile(authProfile)),
  );
  const [chatHistory, setChatHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [conversationTitle, setConversationTitle] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);
  const [reportBaseline, setReportBaseline] = useState(null);
  const [reportReviewMode, setReportReviewMode] = useState('translated');
  const [reportSourceLanguage, setReportSourceLanguage] = useState('');
  const [reportTargetLanguage, setReportTargetLanguage] = useState('');
  const [reportExcludedSections, setReportExcludedSections] = useState([]);
  const [reportExcludedSectionKeys, setReportExcludedSectionKeys] = useState([]);
  const [reportExcludedPretextIds] = useState([]);
  const [messagesBaseline, setMessagesBaseline] = useState(null);
  const [chatReviewMode, setChatReviewMode] = useState('translated');
  const [exportReport, setExportReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [askAboutServices, setAskAboutServices] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [tier1Draft, setTier1Draft] = useState(null);
  const [operationalMandates, setOperationalMandates] = useState('');
  const [mandatePromptId, setMandatePromptId] = useState(null);
  const [mandatePromptName, setMandatePromptName] = useState(null);
  const [reportInstructions, setReportInstructions] = useState('');
  const [reportPromptId, setReportPromptId] = useState(null);
  const [reportPromptName, setReportPromptName] = useState(null);
  const [tradecraftSourceIds, setTradecraftSourceIds] = useState([]);
  const [wraithGenerationMode, setWraithGenerationMode] = useState('offensive');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [clientName, setClientName] = useState('Client');
  const [projectName, setProjectName] = useState('Consultation');
  const [reportTemplateId, setReportTemplateId] = useState('general_consultant');
  const [reportTemplates, setReportTemplates] = useState([]);
  const [chatLanguage, setChatLanguage] = useState('auto');
  const [customChatLanguage, setCustomChatLanguage] = useState('');
  const [reportLanguage, setReportLanguage] = useState('auto');
  const [customReportLanguage, setCustomReportLanguage] = useState('');
  const [analystLanguage, setAnalystLanguage] = useState('en');
  const [customAnalystLanguage, setCustomAnalystLanguage] = useState('');
  const uiLanguage = resolveUiLanguage(chatLanguage, customChatLanguage);
  const [locale, setLocale] = useState('auto');
  const [languageRegister, setLanguageRegister] = useState('operational');
  const [exportLanguageMode, setExportLanguageMode] = useState('target_only');
  const [preferredExportFormat, setPreferredExportFormat] = useState('docx');
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [glossaryLoading, setGlossaryLoading] = useState(false);
  const [promptLibraryEntries, setPromptLibraryEntries] = useState([]);
  const [promptLibraryLoading, setPromptLibraryLoading] = useState(false);
  const [promptLibraryError, setPromptLibraryError] = useState(null);
  const [savingPromptLibrary, setSavingPromptLibrary] = useState(false);
  const promptLibraryLoadSeqRef = useRef(0);
  const [reportPromptLibraryEntries, setReportPromptLibraryEntries] = useState([]);
  const [reportPromptLibraryLoading, setReportPromptLibraryLoading] = useState(false);
  const [reportPromptLibraryError, setReportPromptLibraryError] = useState(null);
  const [savingReportPromptLibrary, setSavingReportPromptLibrary] = useState(false);
  const reportPromptLibraryLoadSeqRef = useRef(0);
  const applyingReportPromptRef = useRef(false);
  const applyingMandatePromptRef = useRef(false);
  const chatInputRef = useRef(null);
  const reportInstructionsApplyRef = useRef({
    sessionId: null,
    promptId: null,
    promptName: null,
    text: '',
  });
  const mandateApplyRef = useRef({
    sessionId: null,
    promptId: null,
    promptName: null,
    text: '',
  });
  const [appliedMandateText, setAppliedMandateText] = useState('');
  const [chatDraftEpoch, setChatDraftEpoch] = useState(0);
  const effectiveChatDraft = React.useMemo(
    () => resolveEffectiveChatDraft({ mandatePromptId, appliedMandateText, inputMessage }),
    [mandatePromptId, appliedMandateText, inputMessage],
  );

  const syncSessionChatDraft = React.useCallback((text) => {
    const nextText = text || '';
    flushSync(() => {
      setAppliedMandateText(nextText);
      setInputMessage(nextText);
      setChatDraftEpoch((epoch) => epoch + 1);
    });
  }, []);

  const exitConversationModeForApply = React.useCallback(() => {
    if (!conversationModeRef.current) return;
    userExitedConversationModeRef.current = true;
    conversationModeRef.current = false;
    setConversationMode(false);
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        console.log('Recognition stop after apply:', err.message);
      }
    }
    setIsListening(false);
  }, []);

  React.useEffect(() => {
    if (!appliedMandateText.trim()) return;
    if (inputMessage !== appliedMandateText) {
      setInputMessage(appliedMandateText);
    }
  }, [appliedMandateText, inputMessage]);

  const handleChatDraftChange = React.useCallback((value) => {
    setInputMessage(value);
    if (appliedMandateText.trim() || mandatePromptId) {
      setAppliedMandateText(value);
      if (mandateApplyRef.current.sessionId === sessionIdRef.current) {
        mandateApplyRef.current = { ...mandateApplyRef.current, text: value };
      }
    }
  }, [appliedMandateText, mandatePromptId]);
  const handleChatDraftChangeRef = React.useRef(handleChatDraftChange);
  React.useEffect(() => {
    handleChatDraftChangeRef.current = handleChatDraftChange;
  }, [handleChatDraftChange]);

  const focusChatInput = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      chatInputRef.current?.focus();
      chatInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);
  const [savingGlossary, setSavingGlossary] = useState(false);
  const [translatingGlossary, setTranslatingGlossary] = useState(false);
  const [reportQa, setReportQa] = useState(null);
  const [runningReportQa, setRunningReportQa] = useState(false);
  const [translatingChat, setTranslatingChat] = useState(false);
  const [translatingReport, setTranslatingReport] = useState(false);
  const [pretexts, setPretexts] = useState([]);
  const [pretextsLoading, setPretextsLoading] = useState(false);
  const pretextLoadSeqRef = useRef(0);

  const applyDeliverableReportFilters = React.useCallback((reportData) => {
    if (!reportData) return reportData;
    const sanitized = stripAuthorizationFromReportData(reportData);
    const categoryFiltered = filterReportByExcludedSections(sanitized, reportExcludedSections);
    return applyReportExclusions(
      categoryFiltered,
      {
        excludedSectionKeys: reportExcludedSectionKeys,
        excludedPretextIds: reportExcludedPretextIds,
      },
      pretexts,
    );
  }, [
    reportExcludedSections,
    reportExcludedSectionKeys,
    reportExcludedPretextIds,
    pretexts,
  ]);

  const deliverableReport = React.useMemo(
    () => applyDeliverableReportFilters(report),
    [report, applyDeliverableReportFilters],
  );

  const deliverableReportBaseline = React.useMemo(
    () => (reportBaseline ? applyDeliverableReportFilters(reportBaseline) : null),
    [reportBaseline, applyDeliverableReportFilters],
  );

  const deliverableExportReport = React.useMemo(
    () => applyDeliverableReportFilters(exportReport || report),
    [exportReport, report, applyDeliverableReportFilters],
  );
  const [extractingPretexts, setExtractingPretexts] = useState(false);
  const [pretextExtractFeedback, setPretextExtractFeedback] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);

  const showActionFeedback = React.useCallback((message, variant = 'success') => {
    setActionFeedback({ message, variant });
  }, []);
  const [exportingCampaignPack, setExportingCampaignPack] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(null);
  const [classification, setClassification] = useState('UNCLASSIFIED');
  const [customClassification, setCustomClassification] = useState('');
  const [kbStats, setKbStats] = useState(null);
  const [kbSyncing, setKbSyncing] = useState(false);
  const [kbReindexing, setKbReindexing] = useState(false);
  const [ingestUrl, setIngestUrl] = useState('');
  const [ingestingUrl, setIngestingUrl] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const [kbUploadFeedback, setKbUploadFeedback] = useState(null);
  const [kbUploadVisible, setKbUploadVisible] = useState(false);
  const [kbDeletingPath, setKbDeletingPath] = useState(null);
  const [kbNoindexUpdatingPath, setKbNoindexUpdatingPath] = useState(null);
  const [showToolsSidebar, setShowToolsSidebar] = useState(true);
  const [workspacePanels, setWorkspacePanels] = useState(() => loadWorkspacePanels());
  const [savingEngagement, setSavingEngagement] = useState(false);
  const [reportProgressStep, setReportProgressStep] = useState('');
  const [reportProgressPercent, setReportProgressPercent] = useState(null);
  const [reportProgressDetail, setReportProgressDetail] = useState('');
  const [reportProgressSteps, setReportProgressSteps] = useState([]);
  const [reportGenWarnings, setReportGenWarnings] = useState([]);
  const [reportGenFailed, setReportGenFailed] = useState(false);
  const activeJobAbortRef = useRef(null);
  const activeJobMetaRef = useRef(null);
  const [nationStateConsent, setNationStateConsent] = useState(false);
  const nationStateConsentRef = useRef(false);
  const consentLoadSeqRef = useRef(0);
  const [consentName, setConsentName] = useState('');
  const [engagementLoaded, setEngagementLoaded] = useState(false);
  
  // Voice capabilities state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isSpeakingRef = React.useRef(false);
  
  // Sync isSpeaking ref with state for interruption detection
  React.useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);
  
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const conversationModeRef = React.useRef(false); // Continuous conversation mode
  const userExitedConversationModeRef = React.useRef(false);
  
  // Sync conversationMode ref with state
  React.useEffect(() => {
    conversationModeRef.current = conversationMode;
  }, [conversationMode]);

  React.useEffect(() => {
    consultantModeRef.current = consultantMode;
  }, [consultantMode]);

  useEffect(() => {
    if (!isModeAllowedForProfile(authProfile, consultantMode)) {
      const fallbackMode = getDefaultModeForProfile(authProfile);
      setConsultantMode(fallbackMode);
      consultantModeRef.current = fallbackMode;
      const fallbackSpecialty = getDefaultSpecialtyForMode(fallbackMode);
      setConsultantSpecialty(fallbackSpecialty);
      consultantSpecialtyRef.current = fallbackSpecialty;
    }
  }, [authProfile, consultantMode]);

  React.useEffect(() => {
    consultantSpecialtyRef.current = consultantSpecialty;
  }, [consultantSpecialty]);

  React.useEffect(() => {
    const stored = localStorage.getItem(PREFERRED_EXPORT_FORMAT_KEY);
    if (stored && ['docx', 'pdf', 'pptx'].includes(stored)) {
      setPreferredExportFormat(stored);
    }
  }, []);

  React.useEffect(() => {
    if (preferredExportFormat) {
      localStorage.setItem(PREFERRED_EXPORT_FORMAT_KEY, preferredExportFormat);
    }
  }, [preferredExportFormat]);

  React.useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  React.useEffect(() => {
    nationStateConsentRef.current = nationStateConsent;
  }, [nationStateConsent]);

  React.useEffect(() => {
    if (!EXERCISE_CONSULTANT_MODES.has(consultantMode)) {
      return;
    }
    const hasPriorActivity = messages.some((message) => message.role === 'user');
    if (hasPriorActivity && !nationStateConsent) {
      nationStateConsentRef.current = true;
      setNationStateConsent(true);
    }
  }, [consultantMode, messages, nationStateConsent]);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [elevenLabsAvailable, setElevenLabsAvailable] = useState(false);
  const elevenLabsAvailableRef = React.useRef(false);
  const availableVoicesRef = React.useRef([]);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState(null);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const audioRef = useRef(null);
  const latestAssistantMessageRef = useRef(null);
  const mainScrollRef = useRef(null);
  const consentRef = useRef(null);
  const engagementRef = useRef(null);
  const glossaryRef = useRef(null);
  const promptLibraryRef = useRef(null);
  const reportPromptLibraryRef = useRef(null);
  const knowledgeRef = useRef(null);
  const pretextRef = useRef(null);
  const chatSectionRef = useRef(null);
  const reportSectionRef = useRef(null);
  const errorRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingPdfFilesRef = useRef(new Map());
  const [pdfPasswordPrompt, setPdfPasswordPrompt] = useState(null);
  const [pdfPasswordQueue, setPdfPasswordQueue] = useState([]);
  const [policyRefusalHint, setPolicyRefusalHint] = useState(false);
  const [followUpSelection, setFollowUpSelection] = useState(null);
  const isProcessingRef = useRef(false); // Prevent multiple simultaneous requests
  const [startingNewChat, setStartingNewChat] = useState(false);
  const speechTimeoutRef = useRef(null); // For pause detection
  const lastTranscriptRef = useRef(''); // Track last transcript to detect new speech

  const scrollToLatestAssistant = () => {
    latestAssistantMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const persistWorkspacePanels = useCallback((nextPanels) => {
    setWorkspacePanels(nextPanels);
    localStorage.setItem(WORKSPACE_PANELS_STORAGE_KEY, JSON.stringify(nextPanels));
  }, []);

  const ensurePanelVisible = useCallback((panelId) => {
    setShowToolsSidebar(true);
    if (!panelId) return;
    setWorkspacePanels((prev) => {
      const next = { ...prev, [panelId]: true };
      localStorage.setItem(WORKSPACE_PANELS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const goToSection = useCallback((ref, { openTools = false, panelId = null, block = 'start' } = {}) => {
    if (openTools) {
      setShowToolsSidebar(true);
    }
    if (panelId) {
      setWorkspacePanels((prev) => {
        const next = { ...prev, [panelId]: true };
        localStorage.setItem(WORKSPACE_PANELS_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }
    const scroll = () => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block });
    };
    const delay = openTools || panelId ? 220 : 0;
    if (delay) {
      setTimeout(scroll, delay);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(scroll));
    }
  }, []);

  const toggleToolsSidebar = () => {
    setShowToolsSidebar((prev) => !prev);
  };

  const toggleWorkspacePanel = (panelId) => {
    setWorkspacePanels((prev) => {
      const next = { ...prev, [panelId]: !prev[panelId] };
      localStorage.setItem(WORKSPACE_PANELS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const showAllWorkspacePanels = () => {
    const next = getDefaultWorkspacePanels();
    WORKSPACE_PANELS.forEach((panel) => {
      next[panel.id] = true;
    });
    persistWorkspacePanels(next);
  };

  const hideAllWorkspacePanels = () => {
    const next = getDefaultWorkspacePanels();
    Object.keys(next).forEach((key) => {
      next[key] = false;
    });
    persistWorkspacePanels(next);
  };

  const beginCancellableJob = useCallback((jobType, { sessionId = null } = {}) => {
    activeJobAbortRef.current?.abort();
    const abortController = new AbortController();
    activeJobAbortRef.current = abortController;
    activeJobMetaRef.current = { jobType, sessionId };
    return abortController;
  }, []);

  const endCancellableJob = useCallback(() => {
    activeJobAbortRef.current = null;
    activeJobMetaRef.current = null;
  }, []);

  const isJobCancelledError = (err) =>
    err?.name === 'AbortError'
    || (typeof err?.message === 'string' && /cancelled/i.test(err.message));

  const handleCancelActiveJob = useCallback(async () => {
    const meta = activeJobMetaRef.current;
    activeJobAbortRef.current?.abort();
    if (!meta?.jobType) return;
    try {
      await apiFetch('/api/jobs/cancel', {
        method: 'POST',
        body: JSON.stringify({
          job_type: meta.jobType,
          session_id: meta.sessionId || undefined,
        }),
      });
    } catch (err) {
      console.warn('Job cancel request failed:', err);
    }
  }, []);

  const cancellableActivity = (label, progress = null, extra = {}) => ({
    label,
    progress,
    onCancel: handleCancelActiveJob,
    ...extra,
  });

  const activeActivity = useMemo(() => {
    if (generatingReport && reportProgressStep) {
      return cancellableActivity(reportProgressStep, reportProgressPercent, {
        warnings: [...hostActivityWarnings, ...reportGenWarnings],
        detail: reportProgressDetail,
        steps: reportProgressSteps,
        startedAt: reportStartedAt,
      });
    }
    if (loading) {
      return cancellableActivity('Consultant is thinking…', null, {
        startedAt: chatJobStartedAt,
        warnings: hostActivityWarnings,
      });
    }
    if (extractingPretexts) {
      return cancellableActivity('Extracting pretexts from chat…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (exportingCampaignPack) {
      return cancellableActivity('Building campaign pack export…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (translatingGlossary) {
      return cancellableActivity('Generating client-language glossary translations…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (translatingChat) {
      return cancellableActivity('Translating chat to client language…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (translatingReport) {
      return cancellableActivity('Translating report…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (runningReportQa) {
      return cancellableActivity('Running translation QA…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (downloadingReport) {
      return cancellableActivity(`Preparing ${downloadingReport.toUpperCase()} download…`, null, {
        warnings: hostActivityWarnings,
      });
    }
    if (uploadingFile) {
      return cancellableActivity('Uploading files…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (kbReindexing) {
      return cancellableActivity('Rebuilding knowledge base index…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (kbSyncing) {
      return cancellableActivity('Syncing knowledge base…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (kbUploading) {
      return cancellableActivity('Uploading to knowledge base…', null, {
        warnings: hostActivityWarnings,
      });
    }
    if (ingestingUrl) {
      return cancellableActivity('Ingesting URL into knowledge base…', null, {
        warnings: hostActivityWarnings,
      });
    }
    return null;
  }, [
    generatingReport,
    reportProgressStep,
    reportProgressPercent,
    reportProgressDetail,
    reportProgressSteps,
    reportGenWarnings,
    hostActivityWarnings,
    reportStartedAt,
    handleCancelActiveJob,
    extractingPretexts,
    exportingCampaignPack,
    translatingGlossary,
    translatingChat,
    translatingReport,
    runningReportQa,
    downloadingReport,
    uploadingFile,
    kbSyncing,
    kbReindexing,
    kbUploading,
    ingestingUrl,
    loading,
    chatJobStartedAt,
  ]);

  const loadReportTemplates = async (mode = consultantModeRef.current) => {
    try {
      const response = await apiFetch(
        `/api/report-templates?mode=${mode}&specialty=${consultantSpecialtyRef.current}`,
      );
      if (!response.ok) return;
      const data = await response.json();
      const templates = data.templates || [];
      setReportTemplates(templates);
      setReportTemplateId((current) => {
        if (!templates.length) return current;
        return templates.some((item) => item.id === current) ? current : templates[0].id;
      });
    } catch (err) {
      console.error('Failed to load report templates:', err);
    }
  };

  const loadEngagementForSession = async (targetSessionId = sessionIdRef.current) => {
    if (!targetSessionId) return;
    try {
      const response = await apiFetch(`/api/engagements/${targetSessionId}`);
      if (!response.ok) return;
      const data = await parseJsonResponse(response);
      setClientName(data.client_name || 'Client');
      setProjectName(data.project_name || 'Consultation');
      setReportTemplateId(data.report_template_id || 'general_consultant');
      const chatLang = hydrateLanguageSelect(data.chat_language || 'auto');
      setChatLanguage(chatLang.select);
      setCustomChatLanguage(chatLang.custom);
      const reportLang = hydrateLanguageSelect(data.report_language || 'auto');
      setReportLanguage(reportLang.select);
      setCustomReportLanguage(reportLang.custom);
      const analystLang = hydrateLanguageSelect(data.analyst_language || 'en');
      setAnalystLanguage(analystLang.select);
      setCustomAnalystLanguage(analystLang.custom);
      setLocale(data.locale || 'auto');
      setLanguageRegister(data.language_register || 'operational');
      const classSel = hydrateClassificationSelect(data.classification, data.classification_banner);
      setClassification(classSel.select);
      setCustomClassification(classSel.custom);
      setReportExcludedSections(data.report_excluded_sections || []);
      setPreferredExportFormat(data.preferred_export_format || 'docx');
      setEngagementLoaded(true);
    } catch (err) {
      console.error('Failed to load engagement:', err);
    }
  };

  const loadGlossary = async () => {
    setGlossaryLoading(true);
    try {
      const response = await apiFetch('/api/glossary');
      if (!response.ok) {
        const errorData = await parseJsonResponse(response);
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorLoadGlossary'));
      }
      const data = await parseJsonResponse(response);
      if (!data.terms) {
        throw new Error(tUi(uiLanguage, 'appErrorLoadGlossary'));
      }
      setGlossaryTerms(data.terms || []);
    } catch (err) {
      console.error('Failed to load glossary:', err);
      setError(err.message || tUi(uiLanguage, 'appErrorLoadGlossary'));
    } finally {
      setGlossaryLoading(false);
    }
  };

  const saveGlossary = async (terms) => {
    setSavingGlossary(true);
    try {
      const response = await apiFetch('/api/glossary', {
        method: 'PUT',
        body: JSON.stringify({ terms }),
      });
      if (!response.ok) throw new Error(tUi(uiLanguage, 'appErrorSaveGlossary'));
      const data = await response.json();
      setGlossaryTerms(data.terms || []);
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorSaveGlossary'));
    } finally {
      setSavingGlossary(false);
    }
  };

  const loadPromptLibrary = async () => {
    const loadSeq = ++promptLibraryLoadSeqRef.current;
    setPromptLibraryLoading(true);
    setPromptLibraryError(null);
    try {
      const response = await apiFetch('/api/prompt-library?category=mandate');
      if (loadSeq !== promptLibraryLoadSeqRef.current) return;
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setPromptLibraryEntries([]);
          if (response.status === 401 && getStoredAuthToken()) {
            setPromptLibraryError(tUi(uiLanguage, 'appErrorLoadPromptLibrary'));
          }
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorLoadPromptLibrary')),
        );
      }
      const data = await response.json();
      setPromptLibraryEntries((data.prompts || []).filter((item) => item.category === 'mandate'));
    } catch (err) {
      if (loadSeq !== promptLibraryLoadSeqRef.current) return;
      console.error('Failed to load prompt library:', err);
      setPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorLoadPromptLibrary'));
    } finally {
      if (loadSeq === promptLibraryLoadSeqRef.current) {
        setPromptLibraryLoading(false);
      }
    }
  };

  const savePromptLibraryEntry = async ({ name, category, content }) => {
    promptLibraryLoadSeqRef.current += 1;
    setSavingPromptLibrary(true);
    setPromptLibraryError(null);
    try {
      const response = await apiFetch('/api/prompt-library', {
        method: 'POST',
        body: JSON.stringify({ name, category: 'mandate', content }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorSavePromptLibrary')),
        );
      }
      const entry = await response.json();
      setPromptLibraryEntries((prev) => {
        const existing = prev || [];
        const mandateOnly = existing.filter((item) => item.category === 'mandate');
        if (mandateOnly.some((item) => item.id === entry.id)) return mandateOnly;
        return [...mandateOnly, entry];
      });
      showActionFeedback(tUi(uiLanguage, 'promptLibrarySaved'));
      return entry;
    } catch (err) {
      setPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorSavePromptLibrary'));
      return null;
    } finally {
      setSavingPromptLibrary(false);
    }
  };

  const applyPromptLibraryEntry = async (prompt) => {
    if (!sessionIdRef.current) {
      const message = tUi(uiLanguage, 'appErrorApplyPromptNoSession');
      setPromptLibraryError(message);
      showActionFeedback(message, 'error');
      return;
    }
    applyingMandatePromptRef.current = true;
    setSavingPromptLibrary(true);
    setPromptLibraryError(null);
    try {
      const response = await apiFetch(`/api/prompt-library/${prompt.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionIdRef.current }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorApplyPromptLibrary')),
        );
      }
      const data = await response.json();
      const appliedId = data.prompt_id || data.prompt?.id || prompt.id;
      const appliedName = data.prompt_name || data.prompt?.name || prompt.name;
      let appliedText = resolveAppliedSessionChatText(data, prompt, promptLibraryEntries);
      if (!appliedText) {
        const convResponse = await apiFetch(`/api/conversations/${sessionIdRef.current}`);
        if (convResponse.ok) {
          const conversation = await convResponse.json();
          appliedText = (conversation.operational_mandates || '').trim();
        }
      }
      if (!appliedText) {
        throw new Error(tUi(uiLanguage, 'appErrorApplyPromptLibrary'));
      }
      mandateApplyRef.current = {
        sessionId: sessionIdRef.current,
        promptId: appliedId,
        promptName: appliedName,
        text: appliedText,
      };
      flushSync(() => {
        setMandatePromptId(appliedId);
        setMandatePromptName(appliedName);
        setAppliedMandateText(appliedText);
        setInputMessage(appliedText);
        setChatDraftEpoch((epoch) => epoch + 1);
      });
      if (canViewPromptLibraryContent(authProfile)) {
        setOperationalMandates(appliedText);
      }
      exitConversationModeForApply();
      goToSection(chatSectionRef);
      focusChatInput();
      const feedbackParts = [
        tUi(uiLanguage, 'promptLibraryAppliedNamed', { name: appliedName }),
        tUi(uiLanguage, 'promptLibraryAppliedClickSend'),
      ];
      if (consultantModeRef.current !== 'nation_state') {
        feedbackParts.push(tUi(uiLanguage, 'promptLibraryAppliedNationStateHint'));
      }
      showActionFeedback(feedbackParts.join(' '));
    } catch (err) {
      const message = err.message || tUi(uiLanguage, 'appErrorApplyPromptLibrary');
      setPromptLibraryError(message);
      showActionFeedback(message, 'error');
    } finally {
      applyingMandatePromptRef.current = false;
      setSavingPromptLibrary(false);
    }
  };

  const updatePromptLibraryEntry = async (promptId, { name, category, content }) => {
    promptLibraryLoadSeqRef.current += 1;
    setSavingPromptLibrary(true);
    setPromptLibraryError(null);
    try {
      const response = await apiFetch(`/api/prompt-library/${promptId}`, {
        method: 'PUT',
        body: JSON.stringify({ name, category: 'mandate', content }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorSavePromptLibrary')),
        );
      }
      const entry = await response.json();
      setPromptLibraryEntries((prev) =>
        (prev || [])
          .filter((item) => item.category === 'mandate')
          .map((item) => (item.id === entry.id ? entry : item)),
      );
      if (mandatePromptId === entry.id) {
        setMandatePromptName(entry.name);
        if (canWritePromptLibrary(authProfile)) {
          setOperationalMandates(entry.content || '');
        }
      }
      showActionFeedback(tUi(uiLanguage, 'promptLibraryUpdated'));
      return entry;
    } catch (err) {
      setPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorSavePromptLibrary'));
      return null;
    } finally {
      setSavingPromptLibrary(false);
    }
  };

  const deletePromptLibraryEntry = async (promptId) => {
    promptLibraryLoadSeqRef.current += 1;
    setSavingPromptLibrary(true);
    setPromptLibraryError(null);
    try {
      const response = await apiFetch(`/api/prompt-library/${promptId}?category=mandate`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorSavePromptLibrary')),
        );
      }
      const data = await response.json();
      setPromptLibraryEntries((data.prompts || []).filter((item) => item.category === 'mandate'));
    } catch (err) {
      setPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorSavePromptLibrary'));
    } finally {
      setSavingPromptLibrary(false);
    }
  };

  const loadReportPromptLibrary = async () => {
    const loadSeq = ++reportPromptLibraryLoadSeqRef.current;
    setReportPromptLibraryLoading(true);
    setReportPromptLibraryError(null);
    try {
      const response = await apiFetch('/api/prompt-library?category=report');
      if (loadSeq !== reportPromptLibraryLoadSeqRef.current) return;
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setReportPromptLibraryEntries([]);
          if (response.status === 401 && getStoredAuthToken()) {
            setReportPromptLibraryError(tUi(uiLanguage, 'appErrorLoadPromptLibrary'));
          }
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorLoadPromptLibrary')),
        );
      }
      const data = await response.json();
      setReportPromptLibraryEntries((data.prompts || []).filter((item) => item.category === 'report'));
    } catch (err) {
      if (loadSeq !== reportPromptLibraryLoadSeqRef.current) return;
      console.error('Failed to load report prompt library:', err);
      setReportPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorLoadPromptLibrary'));
    } finally {
      if (loadSeq === reportPromptLibraryLoadSeqRef.current) {
        setReportPromptLibraryLoading(false);
      }
    }
  };

  const saveReportPromptLibraryEntry = async ({ name, category, content }) => {
    reportPromptLibraryLoadSeqRef.current += 1;
    setSavingReportPromptLibrary(true);
    setReportPromptLibraryError(null);
    try {
      const response = await apiFetch('/api/prompt-library', {
        method: 'POST',
        body: JSON.stringify({ name, category: 'report', content }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorSavePromptLibrary')),
        );
      }
      const entry = await response.json();
      setReportPromptLibraryEntries((prev) => {
        const existing = prev || [];
        const reportOnly = existing.filter((item) => item.category === 'report');
        if (reportOnly.some((item) => item.id === entry.id)) return reportOnly;
        return [...reportOnly, entry];
      });
      showActionFeedback(tUi(uiLanguage, 'promptLibrarySaved'));
      return entry;
    } catch (err) {
      setReportPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorSavePromptLibrary'));
      return null;
    } finally {
      setSavingReportPromptLibrary(false);
    }
  };

  const beginApplyingReportPrompt = () => {
    applyingReportPromptRef.current = true;
  };

  const resolveAppliedReportInstructions = (data, prompt, libraryEntries = []) => {
    const appliedCategory = (data.category || prompt.category || 'report').trim().toLowerCase();
    if (appliedCategory !== 'report') return '';
    const libraryMatch = (libraryEntries || []).find((item) => item.id === (data.prompt_id || prompt.id));
    return (
      data.report_instructions
      || data.prompt_text
      || data.text
      || data.prompt?.content
      || prompt.content
      || libraryMatch?.content
      || ''
    ).trim();
  };

  const syncReportInstructionsFromConversation = (conversation) => {
    if (!conversation) return;
    const instructions = (conversation.report_instructions || '').trim();
    if (!instructions) return;
    setReportInstructions(instructions);
    reportInstructionsApplyRef.current = {
      sessionId: conversation.session_id || sessionIdRef.current,
      promptId: conversation.report_prompt_id || null,
      promptName: conversation.report_prompt_name || null,
      text: instructions,
    };
  };

  const applyReportPromptLibraryEntry = async (prompt) => {
    if (!sessionIdRef.current) {
      const message = tUi(uiLanguage, 'appErrorApplyPromptNoSession');
      setReportPromptLibraryError(message);
      showActionFeedback(message, 'error');
      return;
    }
    applyingReportPromptRef.current = true;
    setSavingReportPromptLibrary(true);
    setReportPromptLibraryError(null);
    try {
      const response = await apiFetch(`/api/prompt-library/${prompt.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionIdRef.current }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorApplyPromptLibrary')),
        );
      }
      const data = await response.json();
      const appliedId = data.prompt_id || data.prompt?.id || prompt.id;
      const appliedName = data.prompt_name || data.prompt?.name || prompt.name;
      const instructionsText = resolveAppliedReportInstructions(
        data,
        prompt,
        reportPromptLibraryEntries,
      );
      reportInstructionsApplyRef.current = {
        sessionId: sessionIdRef.current,
        promptId: appliedId,
        promptName: appliedName,
        text: instructionsText,
      };
      setReportPromptId(appliedId);
      setReportPromptName(appliedName);
      setReportInstructions(instructionsText);
      if (instructionsText && canUseChat(authProfile)) {
        syncSessionChatDraft(instructionsText);
        exitConversationModeForApply();
        goToSection(chatSectionRef);
        focusChatInput();
      }
      const feedbackParts = [
        tUi(uiLanguage, 'promptLibraryAppliedNamed', { name: appliedName }),
      ];
      if (instructionsText && canUseChat(authProfile)) {
        feedbackParts.push(tUi(uiLanguage, 'promptLibraryAppliedClickSend'));
      } else if (instructionsText) {
        const preview = instructionsText.slice(0, 80);
        const previewSuffix = instructionsText.length > 80 ? '…' : '';
        feedbackParts.push(`"${preview}${previewSuffix}"`);
      }
      showActionFeedback(feedbackParts.join(' '));
      // Backend apply endpoint already persisted report_instructions; avoid redundant PUT
      // that can fail/time out on large sessions and surface as a false "network error".
      saveCurrentConversation({
        reportInstructions: instructionsText,
        reportPromptId: appliedId,
        reportPromptName: appliedName,
      }).catch((err) => {
        console.warn('Background conversation sync after report prompt apply failed:', err);
      });
    } catch (err) {
      const message = err.message || tUi(uiLanguage, 'appErrorApplyPromptLibrary');
      setReportPromptLibraryError(message);
      showActionFeedback(message, 'error');
    } finally {
      applyingReportPromptRef.current = false;
      setSavingReportPromptLibrary(false);
    }
  };

  const updateReportPromptLibraryEntry = async (promptId, { name, category, content }) => {
    reportPromptLibraryLoadSeqRef.current += 1;
    setSavingReportPromptLibrary(true);
    setReportPromptLibraryError(null);
    try {
      const response = await apiFetch(`/api/prompt-library/${promptId}`, {
        method: 'PUT',
        body: JSON.stringify({ name, category: 'report', content }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorSavePromptLibrary')),
        );
      }
      const entry = await response.json();
      setReportPromptLibraryEntries((prev) =>
        (prev || [])
          .filter((item) => item.category === 'report')
          .map((item) => (item.id === entry.id ? entry : item)),
      );
      if (reportPromptId === entry.id) {
        setReportPromptName(entry.name);
        if (canWritePromptLibrary(authProfile)) {
          setReportInstructions(entry.content || '');
        }
      }
      showActionFeedback(tUi(uiLanguage, 'promptLibraryUpdated'));
      return entry;
    } catch (err) {
      setReportPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorSavePromptLibrary'));
      return null;
    } finally {
      setSavingReportPromptLibrary(false);
    }
  };

  const deleteReportPromptLibraryEntry = async (promptId) => {
    reportPromptLibraryLoadSeqRef.current += 1;
    setSavingReportPromptLibrary(true);
    setReportPromptLibraryError(null);
    try {
      const response = await apiFetch(`/api/prompt-library/${promptId}?category=report`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorSavePromptLibrary')),
        );
      }
      const data = await response.json();
      setReportPromptLibraryEntries((data.prompts || []).filter((item) => item.category === 'report'));
    } catch (err) {
      setReportPromptLibraryError(err.message || tUi(uiLanguage, 'appErrorSavePromptLibrary'));
    } finally {
      setSavingReportPromptLibrary(false);
    }
  };

  const translateGlossaryToClient = async () => {
    goToSection(glossaryRef, { openTools: true, panelId: 'glossary' });
    setTranslatingGlossary(true);
    setError(null);
    const abortController = beginCancellableJob('glossary_translate', {
      sessionId: sessionIdRef.current,
    });
    try {
      const response = await apiFetch('/api/glossary/translate-to-client', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          source_language: activeUserLanguage(chatLanguage, customChatLanguage),
          target_language: activeClientLanguage(reportLanguage, customReportLanguage),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorTranslateGlossary'));
      }
      const data = await response.json();
      setGlossaryTerms(data.terms || []);
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Glossary translation was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorTranslateGlossary')),
      );
    } finally {
      endCancellableJob();
      setTranslatingGlossary(false);
    }
  };

  const saveEngagement = async () => {
    setSavingEngagement(true);
    try {
      const response = await apiFetch('/api/engagements', {
        method: 'PUT',
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          client_name: clientName,
          project_name: projectName,
          report_template_id: reportTemplateId,
          chat_language: resolveLanguageValue(chatLanguage, customChatLanguage),
          report_language: resolveLanguageValue(reportLanguage, customReportLanguage),
          analyst_language: resolveLanguageValue(analystLanguage, customAnalystLanguage),
          locale,
          language_register: languageRegister,
          report_excluded_sections: reportExcludedSections,
          mode: consultantModeRef.current,
          specialty: consultantSpecialtyRef.current,
          preferred_export_format: preferredExportFormat,
          classification: classification === '__custom__' ? customClassification : classification,
          export_language_mode: exportLanguageMode,
          report_review_mode: reportReviewMode,
          report_source_language: reportSourceLanguage,
          report_target_language: reportTargetLanguage,
          chat_review_mode: chatReviewMode,
          operational_mandates: operationalMandates,
          tradecraft_source_ids: tradecraftSourceIds,
          wraith_generation_mode: wraithGenerationMode,
        }),
      });
      if (!response.ok) throw new Error(tUi(uiLanguage, 'appErrorSaveEngagement'));
      await saveCurrentConversation({ exportLanguageMode }).catch((err) => {
        console.error('Failed to sync export language mode to conversation:', err);
      });
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorSaveEngagement'));
    } finally {
      setSavingEngagement(false);
    }
  };

  const handleExportLanguageModeChange = (mode) => {
    setExportLanguageMode(mode);
    saveCurrentConversation({ exportLanguageMode: mode }).catch((err) => {
      console.error('Failed to save export language mode:', err);
    });
  };

  const resolveSessionId = (targetSessionId) => (
    typeof targetSessionId === 'string' && targetSessionId.trim()
      ? targetSessionId.trim()
      : sessionIdRef.current
  );

  const loadPretexts = async (targetSessionId) => {
    const sessionId = resolveSessionId(targetSessionId);
    if (!sessionId) {
      pretextLoadSeqRef.current += 1;
      setPretexts([]);
      return;
    }
    const loadSeq = ++pretextLoadSeqRef.current;
    setPretextsLoading(true);
    try {
      const response = await apiFetch(`/api/pretexts/${encodeURIComponent(sessionId)}`);
      if (loadSeq !== pretextLoadSeqRef.current) return;
      if (response.status === 404) {
        setPretexts([]);
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorLoadPretexts'));
      }
      setPretexts(await response.json());
    } catch (err) {
      if (loadSeq !== pretextLoadSeqRef.current) return;
      console.error('Failed to load pretexts:', err);
      setError(err.message || tUi(uiLanguage, 'appErrorLoadPretexts'));
    } finally {
      if (loadSeq === pretextLoadSeqRef.current) {
        setPretextsLoading(false);
      }
    }
  };

  const loadKbStats = async () => {
    try {
      const response = await apiFetch('/api/knowledge/stats');
      if (response.ok) {
        const data = await response.json();
        setKbStats(data);
      }
    } catch (err) {
      console.error('Failed to load KB stats:', err);
    }
  };

  const loadNationStateConsent = async (targetSessionId = sessionIdRef.current) => {
    if (!targetSessionId) {
      setNationStateConsent(false);
      return;
    }
    const seq = ++consentLoadSeqRef.current;
    try {
      const response = await apiFetch(`/api/consent/nation-state/${targetSessionId}`);
      if (seq !== consentLoadSeqRef.current) {
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setNationStateConsent(!!data.accepted);
        setConsentName(data.acknowledged_by || '');
      } else {
        setNationStateConsent(false);
      }
    } catch (err) {
      if (seq === consentLoadSeqRef.current) {
        setNationStateConsent(false);
      }
    }
  };

  const handleNationStateConsent = async () => {
    const acknowledgedBy = consentName.trim();
    if (!acknowledgedBy) {
      throw new Error(tUi(uiLanguage, 'appErrorConsentNameRequired'));
    }
    if (!sessionIdRef.current) {
      throw new Error(tUi(uiLanguage, 'appErrorSessionInitializing'));
    }
    const response = await apiFetch('/api/consent/nation-state', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        accepted: true,
        acknowledged_by: acknowledgedBy,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorRecordConsent'));
    }
    consentLoadSeqRef.current += 1;
    nationStateConsentRef.current = true;
    setNationStateConsent(true);
  };

  const handleCreatePretext = async (payload) => {
    pretextLoadSeqRef.current += 1;
    const response = await apiFetch('/api/pretexts', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionIdRef.current, ...payload }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorCreatePretext'));
      setError(message);
      throw new Error(message);
    }
    const created = await response.json();
    setPretexts((prev) => {
      if (prev.some((item) => item.id === created.id)) return prev;
      return [...prev, created];
    });
  };

  const handleUpdatePretext = async (pretextId, payload) => {
    pretextLoadSeqRef.current += 1;
    const response = await apiFetch(`/api/pretexts/${sessionIdRef.current}/${pretextId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorUpdatePretext'));
      setError(message);
      throw new Error(message);
    }
    const updated = await response.json();
    setPretexts((prev) => prev.map((item) => (item.id === pretextId ? updated : item)));
  };

  useEffect(() => {
    if (!actionFeedback) return undefined;
    const timer = window.setTimeout(() => setActionFeedback(null), 8000);
    return () => window.clearTimeout(timer);
  }, [actionFeedback]);

  const handleDeletePretext = async (pretextId) => {
    pretextLoadSeqRef.current += 1;
    const response = await apiFetch(`/api/pretexts/${sessionIdRef.current}/${pretextId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = formatApiErrorDetail(errorData.detail, tUi(uiLanguage, 'appErrorDeletePretext'));
      setError(message);
      throw new Error(message);
    }
    setPretexts((prev) => prev.filter((item) => item.id !== pretextId));
  };

  const handleExtractPretexts = async () => {
    goToSection(pretextRef, { openTools: true, panelId: 'pretext' });
    setExtractingPretexts(true);
    setError(null);
    setPretextExtractFeedback(null);
    const abortController = beginCancellableJob('pretext_extract', {
      sessionId: sessionIdRef.current,
    });
    try {
      await saveCurrentConversation();
      const response = await apiFetch(`/api/pretexts/${sessionIdRef.current}/extract`, {
        method: 'POST',
        signal: abortController.signal,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const message = Array.isArray(detail)
          ? detail.map((item) => item.msg || String(item)).join(', ')
          : (detail || tUi(uiLanguage, 'appErrorExtractPretexts'));
        throw new Error(message);
      }
      const data = await response.json();
      pretextLoadSeqRef.current += 1;
      setPretexts(data.pretexts || []);
      const importedCount = data.imported_count ?? 0;
      const extractedCount = data.extracted_count ?? 0;
      if (importedCount === 0 && extractedCount === 0) {
        setPretextExtractFeedback(
          'No pretexts were found in the chat. Ask the consultant to draft scripts or scenarios, then extract again.',
        );
      } else if (importedCount > 0) {
        setPretextExtractFeedback(
          `Imported ${importedCount} pretext${importedCount === 1 ? '' : 's'}. Review below — edit or delete any that aren't right.`,
        );
      } else {
        setPretextExtractFeedback(
          'No new pretexts to import — everything found was already in your library.',
        );
      }
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Pretext extraction was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorExtractPretexts')),
      );
    } finally {
      endCancellableJob();
      setExtractingPretexts(false);
    }
  };

  const handleExportPretextPack = async () => {
    goToSection(pretextRef, { openTools: true, panelId: 'pretext' });
    setExportingCampaignPack(true);
    const abortController = beginCancellableJob('campaign_pack_export', {
      sessionId: sessionIdRef.current,
    });
    try {
      const params = new URLSearchParams({
        export_language_mode: exportLanguageMode,
      });
      const response = await apiFetch(
        `/api/pretexts/${sessionIdRef.current}/export?${params.toString()}`,
        { signal: abortController.signal },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorExportCampaignPack'));
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const templateLabel = reportTemplates.find((item) => item.id === reportTemplateId)?.label
        || 'Report';
      const filename = parseContentDispositionFilename(disposition)
        || buildExportFilename({
          clientName,
          projectName,
          reportTemplateLabel: templateLabel,
          classification,
          customClassification,
          extension: 'txt',
          reportPayload: report,
        });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Campaign pack export was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorExportCampaignPack')),
      );
    } finally {
      endCancellableJob();
      setExportingCampaignPack(false);
    }
  };

  const handleKbSync = async ({ scrollToPanel = true } = {}) => {
    if (scrollToPanel) {
      goToSection(knowledgeRef, { openTools: true, panelId: 'knowledge' });
    }
    setKbSyncing(true);
    const abortController = beginCancellableJob('kb_sync');
    try {
      const response = await apiFetch('/api/knowledge/sync', {
        method: 'POST',
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error('Knowledge base sync failed');
      await loadKbStats();
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Knowledge base sync was cancelled. You can try again.'
          : (err.message || 'Knowledge base sync failed'),
      );
    } finally {
      endCancellableJob();
      setKbSyncing(false);
    }
  };

  const handleKbReindex = async ({ scrollToPanel = true } = {}) => {
    if (!window.confirm(tUi(uiLanguage, 'appConfirmKbReindex'))) return;
    if (scrollToPanel) {
      goToSection(knowledgeRef, { openTools: true, panelId: 'knowledge' });
    }
    setKbReindexing(true);
    const abortController = beginCancellableJob('kb_reindex');
    try {
      const response = await apiFetch('/api/knowledge/reindex', {
        method: 'DELETE',
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error('Knowledge base reindex failed');
      await loadKbStats();
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Knowledge base reindex was cancelled. You can try again.'
          : (err.message || 'Knowledge base reindex failed'),
      );
    } finally {
      endCancellableJob();
      setKbReindexing(false);
    }
  };

  const handleIngestUrl = async ({ scrollToPanel = true } = {}) => {
    if (scrollToPanel) {
      goToSection(knowledgeRef, { openTools: true, panelId: 'knowledge' });
    }
    setIngestingUrl(true);
    const abortController = beginCancellableJob('kb_ingest_url');
    try {
      const response = await apiFetch('/api/knowledge/ingest-url', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({ url: ingestUrl.trim() }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'URL ingest failed');
      }
      setIngestUrl('');
      await loadKbStats();
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'URL ingest was cancelled. You can try again.'
          : (err.message || 'URL ingest failed'),
      );
    } finally {
      endCancellableJob();
      setIngestingUrl(false);
    }
  };

  const handleKbUpload = async (files, { scrollToPanel = true } = {}) => {
    if (!files || files.length === 0) return;
    if (scrollToPanel) {
      goToSection(knowledgeRef, { openTools: true, panelId: 'knowledge' });
    }
    setKbUploading(true);
    setKbUploadFeedback(null);
    const abortController = beginCancellableJob('kb_upload');
    const noindex = !kbUploadVisible;
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      formData.append('noindex', noindex ? 'true' : 'false');
      const response = await apiFetch('/api/knowledge/upload', {
        method: 'POST',
        signal: abortController.signal,
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatUploadErrorMessage(response, errorData, uiLanguage)
            || tUi(uiLanguage, 'appErrorKbUploadFailed'),
        );
      }
      const data = await response.json();
      const count = data.saved?.length ?? files.length;
      setKbUploadFeedback({
        type: 'success',
        message: noindex
          ? tUi(uiLanguage, 'kbUploadSuccessNoindex', { count })
          : tUi(uiLanguage, 'kbUploadSuccess', { count }),
      });
      await loadKbStats();
      setTimeout(() => setKbUploadFeedback(null), 6000);
    } catch (err) {
      setKbUploadFeedback({
        type: 'error',
        message: isJobCancelledError(err)
          ? 'Knowledge base upload was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorKbUploadFailed')),
      });
    } finally {
      endCancellableJob();
      setKbUploading(false);
    }
  };

  const handleKbDelete = async (path, filename) => {
    const label = filename || path;
    if (!window.confirm(tUi(uiLanguage, 'kbDeleteConfirm', { name: label }))) return;
    setKbDeletingPath(path);
    setKbUploadFeedback(null);
    try {
      const response = await apiFetch('/api/knowledge/files', {
        method: 'DELETE',
        body: JSON.stringify({ path }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, 'Failed to delete knowledge base file.'),
        );
      }
      await loadKbStats();
    } catch (err) {
      setKbUploadFeedback({
        type: 'error',
        message: err.message || 'Failed to delete knowledge base file.',
      });
    } finally {
      setKbDeletingPath(null);
    }
  };

  const handleKbNoindexToggle = async (path, noindex) => {
    setKbNoindexUpdatingPath(path);
    try {
      const response = await apiFetch('/api/knowledge/files/noindex', {
        method: 'PUT',
        body: JSON.stringify({ path, noindex }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, 'Failed to update noindex setting.'),
        );
      }
      await loadKbStats();
    } catch (err) {
      setKbUploadFeedback({
        type: 'error',
        message: err.message || 'Failed to update noindex setting.',
      });
    } finally {
      setKbNoindexUpdatingPath(null);
    }
  };

  // Stop speaking function (defined early)
  const stopSpeaking = React.useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (synthRef.current) {
      synthRef.current.cancel();
    }
    setIsSpeaking(false);
  }, []);

  // Text-to-speech function (uses ElevenLabs if available, falls back to Web Speech API)
  const speakText = React.useCallback(async (text) => {
    if (!voiceEnabled) {
      return Promise.resolve();
    }
    
    // Stop any ongoing speech
    stopSpeaking();
    
    // Get current values directly from refs and localStorage (avoid closure issues)
    const currentVoiceId = selectedVoiceId || localStorage.getItem('elevenlabs_voice_id');
    // Use ref to get current value (always up-to-date)
    // If we have a voice ID and ElevenLabs is available, use it (most reliable check)
    const isElevenLabsAvailable = elevenLabsAvailableRef.current && currentVoiceId;
    
    console.log('🔊 speakText called:', {
      textLength: text.length,
      elevenLabsAvailable: isElevenLabsAvailable,
      elevenLabsAvailableRef: elevenLabsAvailableRef.current,
      availableVoicesCount: availableVoices.length,
      availableVoicesRefCount: availableVoicesRef.current.length,
      selectedVoiceId: selectedVoiceId,
      currentVoiceId: currentVoiceId,
      voiceIdFromLocalStorage: localStorage.getItem('elevenlabs_voice_id'),
      voiceEnabled: voiceEnabled
    });
    
    return new Promise((resolve) => {
      // Use ElevenLabs if available - must have ref true, voices available, AND a voice ID
      if (isElevenLabsAvailable && currentVoiceId) {
        console.log('✅ Using ElevenLabs with voice:', currentVoiceId);
        setIsSpeaking(true);
        
        apiFetch('/api/text-to-speech', {
          method: 'POST',
          body: JSON.stringify({
            text: text,
            voice_id: currentVoiceId
          }),
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to generate speech');
          }
          return response.json();
        })
        .then(data => {
          // Create audio element and play
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current = audio;
          
          audio.onended = () => {
            setIsSpeaking(false);
            audioRef.current = null;
            resolve();
          };
          
          audio.onerror = (err) => {
            console.error('Audio playback error:', err);
            setIsSpeaking(false);
            audioRef.current = null;
            // Fall back to Web Speech API
            if (synthRef.current) {
              speakTextWebSpeech(text).then(resolve);
            } else {
              resolve();
            }
          };
          
          audio.play().catch(err => {
            console.error('Audio play error:', err);
            setIsSpeaking(false);
            audioRef.current = null;
            // Fall back to Web Speech API
            if (synthRef.current) {
              speakTextWebSpeech(text).then(resolve);
            } else {
              resolve();
            }
          });
        })
        .catch(err => {
          console.error('❌ ElevenLabs TTS error:', err);
          setIsSpeaking(false);
          // Fall back to Web Speech API
          if (synthRef.current) {
            console.log('⚠️ Falling back to Web Speech API due to error');
            speakTextWebSpeech(text).then(resolve);
          } else {
            resolve();
          }
        });
      } else if (synthRef.current) {
        // Fallback to Web Speech API
        console.log('⚠️ Falling back to Web Speech API:', {
          reason: !isElevenLabsAvailable ? 'ElevenLabs not available' : 'No voice ID selected',
          elevenLabsAvailable: isElevenLabsAvailable,
          voiceId: currentVoiceId
        });
        speakTextWebSpeech(text).then(resolve);
      } else {
        console.log('⚠️ No TTS available');
        resolve();
      }
    });
  }, [voiceEnabled, elevenLabsAvailable, selectedVoiceId, availableVoices, stopSpeaking]);
  
  // Web Speech API fallback function
  const speakTextWebSpeech = React.useCallback((text) => {
    if (!synthRef.current) {
      return Promise.resolve();
    }
    
    return new Promise((resolve) => {
      // Cancel any ongoing speech
      synthRef.current.cancel();
      
      // Clean up text (remove markdown, special characters that don't read well)
      // But preserve natural pauses and flow
      const cleanText = text
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
        .replace(/\*(.*?)\*/g, '$1') // Remove italic markdown
        .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove markdown links
        .replace(/#{1,6}\s/g, '') // Remove markdown headers
        .replace(/\n{3,}/g, '. ') // Convert multiple newlines to pauses
        .replace(/\n/g, '. ') // Convert single newlines to pauses
        .replace(/\.{3,}/g, '.') // Clean up multiple periods
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
      
      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      // More human-like, soothing settings
      utterance.rate = 0.85; // Slower, more natural pace
      utterance.pitch = 0.95; // Slightly lower pitch (more soothing)
      utterance.volume = 0.9; // Slightly softer volume
      
      // Try to find the most natural, human-sounding voice
      const voices = synthRef.current.getVoices();
      
      // Priority list: prefer neural/neural2 voices (most natural), then specific high-quality voices
      const voicePriority = [
        // Neural voices (most natural)
        voice => voice.name.includes('Neural') || voice.name.includes('neural'),
        // Google Cloud voices (very natural)
        voice => voice.name.includes('Google') && (voice.name.includes('Neural') || voice.name.includes('Wavenet')),
        // Microsoft natural voices
        voice => voice.name.includes('Microsoft') && (voice.name.includes('Neural') || voice.name.includes('Zira') || voice.name.includes('Aria')),
        // High-quality system voices
        voice => voice.name.includes('Samantha'), // macOS (very natural)
        voice => voice.name.includes('Alex'), // macOS
        voice => voice.name.includes('Victoria'), // macOS
        voice => voice.name.includes('Karen'), // macOS
        voice => voice.name.includes('Moira'), // macOS
        voice => voice.name.includes('Tessa'), // macOS
        // Windows natural voices
        voice => voice.name.includes('Zira') || voice.name.includes('Aria'),
        // Google voices
        voice => voice.name.includes('Google'),
        // Microsoft voices
        voice => voice.name.includes('Microsoft'),
        // Any female voice (often more natural for conversational AI)
        voice => voice.name.toLowerCase().includes('female') || 
                 ['Samantha', 'Victoria', 'Karen', 'Moira', 'Tessa', 'Zira', 'Aria', 'Susan', 'Linda'].some(name => 
                   voice.name.includes(name)
                 ),
      ];
      
      let selectedVoice = null;
      for (const priorityCheck of voicePriority) {
        selectedVoice = voices.find(priorityCheck);
        if (selectedVoice) {
          break;
        }
      }
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        console.log('Using Web Speech API voice:', selectedVoice.name);
      } else if (voices.length > 0) {
        // Fallback: use first available voice
        utterance.voice = voices[0];
        console.log('Using fallback voice:', voices[0].name);
      }
      
      utterance.onstart = () => {
        setIsSpeaking(true);
      };
      
      utterance.onend = () => {
        setIsSpeaking(false);
        resolve();
      };
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setIsSpeaking(false);
        resolve();
      };
      
      synthRef.current.speak(utterance);
    });
  }, [stopSpeaking]);

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant') return;
    requestAnimationFrame(() => {
      scrollToLatestAssistant();
    });
  }, [messages]);

  useEffect(() => {
    if (!error) return;
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [error]);

  // Load saved chat history and restore the active session
  useEffect(() => {
    const initializeApp = async () => {
      setHistoryLoading(true);
      try {
        const conversations = await loadChatHistory();
        const activeSession = getStoredActiveSession(authProfile?.tenant_id) || sessionIdRef.current;
        const hasActive = Boolean(activeSession)
          && conversations.some((item) => item.session_id === activeSession);

        if (hasActive) {
          await loadConversationById(activeSession);
        } else if (conversations.length > 0) {
          await loadConversationById(conversations[0].session_id);
        } else {
          const initialMode = getDefaultModeForProfile(authProfile);
          const initialSpecialty = getDefaultSpecialtyForMode(initialMode);
          const newSessionId = await createSessionFromApi(
            initialMode,
            initialSpecialty,
          );
          setStoredActiveSession(authProfile?.tenant_id, newSessionId);
          resetConversation(
            initialMode,
            initialSpecialty,
            newSessionId,
          );
          await saveCurrentConversation();
        }
      } catch (err) {
        console.error('Failed to initialize chat history:', err);
        try {
          const initialMode = getDefaultModeForProfile(authProfile);
          const initialSpecialty = getDefaultSpecialtyForMode(initialMode);
          const newSessionId = await createSessionFromApi(
            initialMode,
            initialSpecialty,
          );
          setStoredActiveSession(authProfile?.tenant_id, newSessionId);
          resetConversation(
            initialMode,
            initialSpecialty,
            newSessionId,
          );
        } catch (sessionErr) {
          console.error('Failed to create session:', sessionErr);
        }
      } finally {
        setHistoryLoading(false);
      }
    };

    initializeApp();
    loadReportTemplates();
    loadKbStats();
    loadGlossary();
    loadPromptLibrary();
    loadReportPromptLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!workspacePanels.promptLibrary) return;
    loadPromptLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspacePanels.promptLibrary]);

  useEffect(() => {
    if (!workspacePanels.reportPromptLibrary) return;
    loadReportPromptLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspacePanels.reportPromptLibrary]);

  useEffect(() => {
    loadEngagementForSession(sessionId);
    loadPretexts(sessionId);
    loadNationStateConsent(sessionId);
  }, [sessionId]);

  useEffect(() => {
    loadReportTemplates(consultantMode);
  }, [consultantMode, consultantSpecialty]);

  // Check ElevenLabs availability and load voices
  useEffect(() => {
    const checkElevenLabs = async () => {
      try {
        const response = await apiFetch('/api/voices');
        const data = await response.json();
        
        if (data.available && data.voices && data.voices.length > 0) {
          setElevenLabsAvailable(true);
          elevenLabsAvailableRef.current = true;
          setAvailableVoices(data.voices);
          availableVoicesRef.current = data.voices; // Update ref immediately
          
          // Load saved voice preference from localStorage
          const savedVoiceId = localStorage.getItem('elevenlabs_voice_id');
          if (savedVoiceId) {
            // Verify the saved voice still exists
            const savedVoice = data.voices.find(v => v.voice_id === savedVoiceId);
            if (savedVoice) {
              setSelectedVoiceId(savedVoiceId);
              console.log('Loaded saved voice preference:', savedVoice.name);
            } else {
              // Saved voice no longer available, use default
              const defaultVoice = data.voices.find(v => 
                v.name.toLowerCase().includes('rachel') ||
                v.name.toLowerCase().includes('sarah') ||
                v.name.toLowerCase().includes('emily') ||
                v.name.toLowerCase().includes('female')
              ) || data.voices[0];
              setSelectedVoiceId(defaultVoice.voice_id);
              localStorage.setItem('elevenlabs_voice_id', defaultVoice.voice_id);
            }
          } else {
            // No saved preference, use default
            const defaultVoice = data.voices.find(v => 
              v.name.toLowerCase().includes('rachel') ||
              v.name.toLowerCase().includes('sarah') ||
              v.name.toLowerCase().includes('emily') ||
              v.name.toLowerCase().includes('female')
            ) || data.voices[0];
            setSelectedVoiceId(defaultVoice.voice_id);
            localStorage.setItem('elevenlabs_voice_id', defaultVoice.voice_id);
          }
          
          console.log('ElevenLabs available with', data.voices.length, 'voices');
        } else {
          setElevenLabsAvailable(false);
          elevenLabsAvailableRef.current = false;
          availableVoicesRef.current = [];
          console.log('ElevenLabs not available:', data.message);
        }
      } catch (err) {
        console.error('Error checking ElevenLabs:', err);
        setElevenLabsAvailable(false);
        elevenLabsAvailableRef.current = false;
        availableVoicesRef.current = [];
      }
    };
    
    checkElevenLabs();
  }, []);

  // Initialize speech recognition and text-to-speech (fallback)
  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const speechSynthesis = window.speechSynthesis;
    
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true; // Keep listening continuously
      recognition.interimResults = true; // Get interim results for real-time feedback
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onresult = async (event) => {
        console.log('Recognition result event:', event.results.length, 'results');
        
        // Get all results to build complete transcript
        let fullTranscript = '';
        let hasFinalResult = false;
        let finalTranscript = '';
        
        // Build transcript from all results
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript;
          fullTranscript += transcript;
          console.log(`Result ${i}: isFinal=${result.isFinal}, transcript="${transcript}"`);
          
          if (result.isFinal) {
            hasFinalResult = true;
            // Get the final transcript (accumulate all final results)
            finalTranscript = '';
            for (let j = 0; j <= i; j++) {
              if (event.results[j].isFinal) {
                finalTranscript += event.results[j][0].transcript;
              }
            }
            finalTranscript = finalTranscript.trim();
            console.log('Final transcript built:', finalTranscript);
          }
        }
        
        const currentTranscript = fullTranscript.trim();
        console.log('Current full transcript:', currentTranscript, 'Has final:', hasFinalResult);
        
        // If user starts speaking (actual speech detected) while AI is speaking, interrupt the AI
        // Only interrupt if we have meaningful speech (at least 2 characters to avoid noise)
        if (isSpeakingRef.current && currentTranscript && currentTranscript.length >= 2) {
          console.log('🛑 User speech detected while AI is speaking - interrupting AI speech');
          stopSpeaking();
        }
        
        // Update input field for visual feedback (always update for visual feedback)
        if (currentTranscript) {
          handleChatDraftChangeRef.current(currentTranscript);
        }
        
        // Clear any existing timeout
        if (speechTimeoutRef.current) {
          clearTimeout(speechTimeoutRef.current);
          speechTimeoutRef.current = null;
        }
        
        // In conversation mode, handle auto-submit on pause detection
        const isInConversationMode = conversationModeRef.current;
        
        console.log('🔍 Recognition check:', {
          isInConversationMode,
          conversationMode,
          continuous: recognitionRef.current?.continuous,
          isProcessing: isProcessingRef.current,
          hasTranscript: !!currentTranscript,
          transcriptLength: currentTranscript?.length || 0
        });
        
        if (isInConversationMode && !isProcessingRef.current && currentTranscript && currentTranscript.length > 0) {
          console.log('Processing in conversation mode. Has final:', hasFinalResult, 'Transcript:', currentTranscript);
          
          // If we have a final result, user stopped speaking - submit after short delay
          if (hasFinalResult && finalTranscript && finalTranscript !== lastTranscriptRef.current) {
            console.log('🎯 FINAL RESULT DETECTED! Setting timeout to submit:', finalTranscript);
            lastTranscriptRef.current = finalTranscript;
            
            // Wait longer after final speech to allow for pauses and slow speakers (2.5 seconds)
            speechTimeoutRef.current = setTimeout(async () => {
              console.log('⏰ Timeout fired! Checking conditions...');
              console.log('isProcessingRef:', isProcessingRef.current);
              console.log('finalTranscript:', finalTranscript);
              console.log('lastTranscriptRef:', lastTranscriptRef.current);
              
              if (!conversationModeRef.current || userExitedConversationModeRef.current) {
                return;
              }
              
              // Double-check conditions before submitting
              if (!isProcessingRef.current && 
                  finalTranscript && 
                  finalTranscript.length > 0 &&
                  finalTranscript === lastTranscriptRef.current) {
                console.log('✅ ALL CONDITIONS MET! Auto-submitting:', finalTranscript);
                speechTimeoutRef.current = null; // Clear before submitting
                await submitVoiceMessage(finalTranscript);
              } else {
                console.log('❌ Conditions not met:', {
                  isProcessing: isProcessingRef.current,
                  hasTranscript: !!finalTranscript,
                  matches: finalTranscript === lastTranscriptRef.current
                });
              }
            }, 2500); // 2.5 seconds pause detection for slow speakers and thoughtful pauses
          } else if (!hasFinalResult && currentTranscript.length > 0) {
            // Interim result - user is still speaking
            console.log('Interim result, setting longer timeout');
            // Set a timeout in case they pause without a final result
            speechTimeoutRef.current = setTimeout(async () => {
              if (!conversationModeRef.current || userExitedConversationModeRef.current) {
                return;
              }
              if (!isProcessingRef.current && 
                  currentTranscript && 
                  currentTranscript.length > 3) {
                console.log('Auto-submitting interim result after timeout:', currentTranscript);
                lastTranscriptRef.current = currentTranscript;
                speechTimeoutRef.current = null; // Clear before submitting
                await submitVoiceMessage(currentTranscript);
              }
            }, 3500); // 3.5 seconds for interim results (longer pause for slow speakers)
          }
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        // Don't stop listening on errors (except critical ones)
        if (event.error === 'not-allowed') {
          setIsListening(false);
          setError('Microphone access denied. Please enable microphone permissions in your browser settings.');
        } else if (event.error === 'no-speech' && !conversationModeRef.current) {
          // Only stop on no-speech if not in conversation mode
          setIsListening(false);
        }
      };
      
      recognition.onend = () => {
        const stillInConversationMode =
          conversationModeRef.current && !userExitedConversationModeRef.current;
        console.log('Recognition ended. Conversation mode:', stillInConversationMode, 'Processing:', isProcessingRef.current, 'Last transcript:', lastTranscriptRef.current);
        
        if (!stillInConversationMode) {
          setIsListening(false);
          return;
        }
        
        // If we have a pending timeout, let it fire first - don't interfere
        if (speechTimeoutRef.current) {
          console.log('⏳ Timeout pending, waiting for it to fire...');
          // Don't restart yet, let the timeout handle submission first
          // But check again after a moment to ensure it fired
          setTimeout(() => {
            if (!conversationModeRef.current || userExitedConversationModeRef.current) {
              return;
            }
            if (speechTimeoutRef.current) {
              console.log('⚠️ Timeout still pending after delay, forcing submission');
              const pendingText = lastTranscriptRef.current;
              if (pendingText && pendingText.trim().length > 0 && !isProcessingRef.current) {
                clearTimeout(speechTimeoutRef.current);
                speechTimeoutRef.current = null;
                submitVoiceMessage(pendingText);
              }
            }
          }, 1500); // Wait 1.5 seconds for timeout to fire
          return;
        }
        
        // If we have pending text in conversation mode and no timeout, submit it immediately
        if (!isProcessingRef.current && lastTranscriptRef.current) {
          const pendingText = lastTranscriptRef.current.trim();
          if (pendingText && pendingText.length > 0) {
            console.log('📤 Auto-submitting on mic end (no timeout):', pendingText);
            submitVoiceMessage(pendingText);
            return; // Don't restart yet, submitVoiceMessage will handle restart after response
          }
        }
        
        // If in conversation mode, try to restart (unless we're processing)
        if (!isProcessingRef.current) {
          // Small delay before restarting to avoid immediate restart loops
          setTimeout(() => {
            if (!conversationModeRef.current || userExitedConversationModeRef.current || isProcessingRef.current) {
              return;
            }
            try {
              console.log('🔄 Restarting recognition after onend');
              recognition.start();
            } catch (err) {
              // If restart fails (e.g., already started), that's okay
              console.log('Recognition restart:', err.message);
            }
          }, 300);
        } else {
          setIsListening(false);
        }
      };
      
      recognitionRef.current = recognition;
    } else {
      setSpeechSupported(false);
      console.warn('Speech recognition not supported in this browser');
    }
    
    if (speechSynthesis) {
      synthRef.current = speechSynthesis;
      
      // Load voices (some browsers need this)
      const loadVoices = () => {
        const voices = speechSynthesis.getVoices();
        if (voices.length > 0) {
          console.log('Web Speech API voices loaded:', voices.length);
        }
      };
      
      // Chrome loads voices asynchronously
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = loadVoices;
      }
      loadVoices(); // Try immediately in case they're already loaded
      
      // Also try loading after a delay (some browsers need more time)
      setTimeout(loadVoices, 1000);
    }
    
    // Cleanup on unmount
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synthRef.current) {
        synthRef.current.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [stopSpeaking]);

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!effectiveChatDraft.trim() || loading || isProcessingRef.current) {
      return;
    }

    // Use the shared submit function
    await submitVoiceMessage(effectiveChatDraft);
  };

  const loadChatHistory = React.useCallback(async () => {
    const response = await apiFetch('/api/conversations');
    if (!response.ok) {
      throw new Error(tUi(uiLanguage, 'appErrorLoadChatHistory'));
    }
    const data = await parseJsonResponse(response);
    setChatHistory(data.conversations || []);
    return data.conversations || [];
  }, []);

  const saveCurrentConversation = React.useCallback(async (overrides = {}) => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) {
      return null;
    }
    let titleToUse = overrides.title ?? conversationTitle;
    if (!titleToUse) {
      titleToUse = generateAutoTitle(clientName, overrides.messages ?? messages);
    }
    const payload = {
      session_id: currentSessionId,
      title: titleToUse || undefined,
      mode: overrides.mode ?? consultantModeRef.current,
      specialty: overrides.specialty ?? consultantSpecialtyRef.current,
      messages: overrides.messages ?? messages,
      uploaded_files: overrides.uploadedFiles ?? uploadedFiles,
      report: overrides.report !== undefined ? overrides.report : report,
      report_baseline: overrides.reportBaseline !== undefined ? overrides.reportBaseline : reportBaseline,
      report_review_mode: overrides.reportReviewMode ?? reportReviewMode,
      report_source_language: overrides.reportSourceLanguage ?? reportSourceLanguage,
      report_target_language: overrides.reportTargetLanguage ?? reportTargetLanguage,
      report_excluded_sections: overrides.reportExcludedSections ?? reportExcludedSections,
      report_excluded_section_keys: overrides.reportExcludedSectionKeys ?? reportExcludedSectionKeys,
      report_excluded_pretext_ids: overrides.reportExcludedPretextIds ?? reportExcludedPretextIds,
      messages_baseline: overrides.messagesBaseline !== undefined ? overrides.messagesBaseline : messagesBaseline,
      chat_review_mode: overrides.chatReviewMode ?? chatReviewMode,
      operational_mandates: overrides.operationalMandates ?? operationalMandates,
      mandate_prompt_id: overrides.mandatePromptId !== undefined ? overrides.mandatePromptId : mandatePromptId,
      mandate_prompt_name: overrides.mandatePromptName !== undefined ? overrides.mandatePromptName : mandatePromptName,
      report_instructions: overrides.reportInstructions ?? reportInstructions,
      report_prompt_id: overrides.reportPromptId !== undefined ? overrides.reportPromptId : reportPromptId,
      report_prompt_name: overrides.reportPromptName !== undefined ? overrides.reportPromptName : reportPromptName,
      tradecraft_source_ids: overrides.tradecraftSourceIds ?? tradecraftSourceIds,
      wraith_generation_mode: overrides.wraithGenerationMode ?? wraithGenerationMode,
      export_language_mode: overrides.exportLanguageMode ?? exportLanguageMode,
      preferred_export_format: overrides.preferredExportFormat ?? preferredExportFormat,
      tier1_draft: overrides.tier1Draft !== undefined ? overrides.tier1Draft : tier1Draft,
    };

    const response = await apiFetch(`/api/conversations/${currentSessionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorSaveConversation'));
    }

    const saved = await response.json();
    setConversationTitle(saved.title || '');
    if (saved.report_instructions != null) {
      syncReportInstructionsFromConversation(saved);
    }
    setStoredActiveSession(authProfile?.tenant_id, currentSessionId);
    await loadChatHistory();
    return saved;
  }, [
    conversationTitle,
    messages,
    uploadedFiles,
    report,
    reportBaseline,
    reportReviewMode,
    reportSourceLanguage,
    reportTargetLanguage,
    reportExcludedSections,
    reportExcludedSectionKeys,
    reportExcludedPretextIds,
    messagesBaseline,
    chatReviewMode,
    operationalMandates,
    mandatePromptId,
    mandatePromptName,
    reportInstructions,
    reportPromptId,
    reportPromptName,
    tradecraftSourceIds,
    wraithGenerationMode,
    exportLanguageMode,
    preferredExportFormat,
    tier1Draft,
    clientName,
    loadChatHistory,
    authProfile?.tenant_id,
  ]);

  const handleSignOut = async () => {
    try {
      await saveCurrentConversation();
    } catch (err) {
      console.warn('Failed to save conversation before sign out:', err);
    }
    onLogout?.();
  };

  const applyConversation = React.useCallback((conversation) => {
    let mode = conversation.mode || getDefaultModeForProfile(authProfile);
    if (!isModeAllowedForProfile(authProfile, mode)) {
      mode = getDefaultModeForProfile(authProfile);
    }
    const specialty = conversation.specialty
      || getDefaultSpecialtyForMode(mode);
    setSessionId(conversation.session_id);
    sessionIdRef.current = conversation.session_id;
    setStoredActiveSession(authProfile?.tenant_id, conversation.session_id);
    setConsultantMode(mode);
    consultantModeRef.current = mode;
    setConsultantSpecialty(specialty);
    consultantSpecialtyRef.current = specialty;
    setMessages(
      conversation.messages?.length
        ? conversation.messages
        : [{
            role: 'assistant',
            content: getWelcomeMessage(mode, activeUserLanguage(chatLanguage, customChatLanguage)),
            timestamp: new Date().toISOString(),
          }]
    );
    setUploadedFiles(conversation.uploaded_files || []);
    setTier1Draft(normalizeTier1Draft(conversation.tier1_draft));
    const appliedLibraryMandate = Boolean(conversation.mandate_prompt_id);
    const hideLibraryMandateBody = appliedLibraryMandate
      && authProfile
      && !canViewPromptLibraryContent(authProfile);
    const preservedMandateApply = mandateApplyRef.current;
    const preservedMandateText = (
      preservedMandateApply.sessionId === conversation.session_id
      && preservedMandateApply.promptId === conversation.mandate_prompt_id
      && preservedMandateApply.text
    ) ? preservedMandateApply.text : '';
    if (hideLibraryMandateBody) {
      setOperationalMandates('');
      setMandatePromptId(conversation.mandate_prompt_id);
      setMandatePromptName(conversation.mandate_prompt_name || null);
    } else {
      const mandateText = conversation.operational_mandates || preservedMandateText || '';
      setOperationalMandates(mandateText);
      setMandatePromptId(conversation.mandate_prompt_id || null);
      setMandatePromptName(conversation.mandate_prompt_name || null);
    }
    const appliedLibraryReportPrompt = Boolean(conversation.report_prompt_id);
    setReportPromptId(conversation.report_prompt_id || null);
    setReportPromptName(conversation.report_prompt_name || null);
    const hideLibraryReportBody = appliedLibraryReportPrompt
      && authProfile
      && !canViewPromptLibraryContent(authProfile);
    const preservedApply = reportInstructionsApplyRef.current;
    const preservedInstructions = (
      preservedApply.sessionId === conversation.session_id
      && preservedApply.promptId === conversation.report_prompt_id
      && preservedApply.text
    ) ? preservedApply.text : '';
    const reportInstructionsText = hideLibraryReportBody
      ? preservedInstructions
      : (conversation.report_instructions || preservedInstructions || '');
    setReportInstructions(reportInstructionsText);
    const mandateChatDraft = conversation.mandate_prompt_id
      ? (hideLibraryMandateBody ? preservedMandateText : ((conversation.operational_mandates || preservedMandateText || '').trim()))
      : '';
    const reportChatDraft = !conversation.mandate_prompt_id && conversation.report_prompt_id
      ? reportInstructionsText.trim()
      : '';
    const sessionChatDraft = mandateChatDraft || reportChatDraft;
    setAppliedMandateText(sessionChatDraft);
    if (sessionChatDraft && canUseChat(authProfile)) {
      setInputMessage(sessionChatDraft);
    }
    setTradecraftSourceIds(conversation.tradecraft_source_ids || []);
    setWraithGenerationMode(conversation.wraith_generation_mode || 'both');
    setReport(conversation.report || null);
    setExportReport(conversation.report || null);
    setReportBaseline(conversation.report_baseline || null);
    setReportReviewMode(
      conversation.report_review_mode
      || (conversation.report_baseline ? 'side_by_side' : 'translated'),
    );
    setExportLanguageMode(conversation.export_language_mode || 'target_only');
    setReportSourceLanguage(conversation.report_source_language || '');
    setReportTargetLanguage(conversation.report_target_language || '');
    setReportExcludedSections(conversation.report_excluded_sections || []);
    setReportExcludedSectionKeys(conversation.report_excluded_section_keys || []);
    setPreferredExportFormat(conversation.preferred_export_format || 'docx');
    setMessagesBaseline(conversation.messages_baseline || null);
    setChatReviewMode(
      conversation.chat_review_mode
      || (conversation.messages_baseline ? 'side_by_side' : 'translated'),
    );
    setShowReport(!!conversation.report);
    setConversationTitle(conversation.title || '');
    setAskAboutServices(false);
    setShowServiceForm(false);
    setError(null);
  }, [authProfile, chatLanguage, customChatLanguage]);

  const loadConversationById = React.useCallback(async (targetSessionId) => {
    const response = await apiFetch(`/api/conversations/${targetSessionId}`);
    if (!response.ok) {
      throw new Error(tUi(uiLanguage, 'appErrorLoadConversation'));
    }
    const conversation = await response.json();
    applyConversation(conversation);
  }, [applyConversation]);

  const resetConversation = React.useCallback((
    modeId = consultantModeRef.current,
    specialtyId = consultantSpecialtyRef.current,
    keepSessionId = sessionIdRef.current,
  ) => {
    setSessionId(keepSessionId);
    sessionIdRef.current = keepSessionId;
    setConsultantMode(modeId);
    consultantModeRef.current = modeId;
    const nextSpecialty = specialtyId || getDefaultSpecialtyForMode(modeId);
    setConsultantSpecialty(nextSpecialty);
    consultantSpecialtyRef.current = nextSpecialty;
    setMessages([{
      role: 'assistant',
      content: getWelcomeMessage(modeId, activeUserLanguage(chatLanguage, customChatLanguage)),
      timestamp: new Date().toISOString()
    }]);
    setReport(null);
    setExportReport(null);
    setReportBaseline(null);
    setReportReviewMode('translated');
    setReportSourceLanguage('');
    setReportTargetLanguage('');
    setReportExcludedSections([]);
    setReportExcludedSectionKeys([]);
    setMessagesBaseline(null);
    setChatReviewMode('translated');
    setShowReport(false);
    setError(null);
    setPolicyRefusalHint(false);
    setAskAboutServices(false);
    setShowServiceForm(false);
    setUploadedFiles([]);
    setTier1Draft(null);
    setInputMessage('');
    setPretexts([]);
    setOperationalMandates('');
    setMandatePromptId(null);
    setMandatePromptName(null);
    setAppliedMandateText('');
    mandateApplyRef.current = { sessionId: null, promptId: null, promptName: null, text: '' };
    setReportInstructions('');
    setReportPromptId(null);
    setReportPromptName(null);
    reportInstructionsApplyRef.current = { sessionId: null, promptId: null, promptName: null, text: '' };
    setTradecraftSourceIds([]);
    setWraithGenerationMode('offensive');
    setConversationTitle('');
  }, [chatLanguage, customChatLanguage]);

  const handleNewConversation = async () => {
    if (startingNewChat) {
      return;
    }
    setStartingNewChat(true);

    activeJobAbortRef.current?.abort();
    endCancellableJob();
    setLoading(false);
    isProcessingRef.current = false;
    stopSpeaking();

    saveCurrentConversation().catch((err) => {
      console.error('Failed to save current conversation before starting a new one:', err);
    });

    const currentMode = consultantModeRef.current;
    const modeToUse = isModeAllowedForProfile(authProfile, currentMode)
      ? currentMode
      : getDefaultModeForProfile(authProfile);
    const specialtyToUse = getDefaultSpecialtyForMode(modeToUse);

    try {
      const newSessionId = await createSessionFromApi(
        modeToUse,
        specialtyToUse,
      );
      setStoredActiveSession(authProfile?.tenant_id, newSessionId);
      pretextLoadSeqRef.current += 1;
      resetConversation(modeToUse, specialtyToUse, newSessionId);
      setShowHistory(false);
      goToSection(chatSectionRef);
    } catch (err) {
      console.error('Failed to create new session:', err);
      setError(err.message || tUi(uiLanguage, 'appErrorCreateSession'));
      return;
    } finally {
      setStartingNewChat(false);
    }

    try {
      await saveCurrentConversation({
        mode: modeToUse,
        specialty: specialtyToUse,
        messages: [{
          role: 'assistant',
          content: getWelcomeMessage(modeToUse, activeUserLanguage(chatLanguage, customChatLanguage)),
          timestamp: new Date().toISOString(),
        }],
        uploadedFiles: [],
        report: null,
      });
    } catch (err) {
      console.error('Failed to create new conversation:', err);
    }
  };

  const handleConsultantModeChange = async (nextMode) => {
    if (nextMode === consultantMode) {
      return;
    }
    if (!isModeAllowedForProfile(authProfile, nextMode)) {
      setError('This product mode is not licensed for your organization.');
      return;
    }

    try {
      await saveCurrentConversation();
    } catch (err) {
      console.error('Failed to save conversation before mode change:', err);
    }

    const nextSpecialty = getDefaultSpecialtyForMode(nextMode);
    setConsultantMode(nextMode);
    consultantModeRef.current = nextMode;
    setConsultantSpecialty(nextSpecialty);
    consultantSpecialtyRef.current = nextSpecialty;
    resetConversation(nextMode, nextSpecialty, sessionIdRef.current);

    try {
      await saveCurrentConversation({
        mode: nextMode,
        specialty: nextSpecialty,
        messages: [{
          role: 'assistant',
          content: getWelcomeMessage(nextMode, activeUserLanguage(chatLanguage, customChatLanguage)),
          timestamp: new Date().toISOString(),
        }],
        uploadedFiles: [],
        report: null,
      });
    } catch (err) {
      console.error('Failed to save conversation after mode change:', err);
    }
  };

  const handleConsultantSpecialtyChange = async (rawSpecialty) => {
    const nextSpecialty = resolveSpecialtyForMode(consultantMode, rawSpecialty);
    if (nextSpecialty === consultantSpecialty) return;
    setConsultantSpecialty(nextSpecialty);
    consultantSpecialtyRef.current = nextSpecialty;
    try {
      await saveCurrentConversation({ specialty: nextSpecialty });
    } catch (err) {
      console.error('Failed to save specialty change:', err);
    }
    loadReportTemplates(consultantMode);
  };

  const handleSelectConversation = async (targetSessionId) => {
    if (targetSessionId === sessionIdRef.current) {
      return;
    }

    try {
      await saveCurrentConversation();
      await loadConversationById(targetSessionId);
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorOpenConversation'));
    }
  };

  const handleDeleteConversation = async (targetSessionId, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    if (!window.confirm(tUi(uiLanguage, 'appConfirmDeleteChat'))) {
      return;
    }

    try {
      const response = await apiFetch(`/api/conversations/${targetSessionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(tUi(uiLanguage, 'appErrorDeleteConversation'));
      }

      const conversations = await loadChatHistory();

      if (targetSessionId === sessionIdRef.current) {
        if (conversations.length > 0) {
          await loadConversationById(conversations[0].session_id);
        } else {
          const newSessionId = await createSessionFromApi(
            consultantMode,
            consultantSpecialtyRef.current,
          );
          setStoredActiveSession(authProfile?.tenant_id, newSessionId);
          resetConversation(
            consultantMode,
            consultantSpecialtyRef.current,
            newSessionId,
          );
          await saveCurrentConversation();
        }
      }
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorDeleteConversation'));
    }
  };

  const handleClearHistory = async () => {
    if (chatHistory.length === 0) {
      return;
    }

    if (
      !window.confirm(
        tUi(uiLanguage, 'appConfirmClearAllHistory'),
      )
    ) {
      return;
    }

    try {
      for (const item of chatHistory) {
        const response = await apiFetch(`/api/conversations/${item.session_id}`, {
          method: 'DELETE',
        });
        if (!response.ok) {
          throw new Error(tUi(uiLanguage, 'appErrorDeleteConversation'));
        }
      }

      const newSessionId = await createSessionFromApi(
        consultantMode,
        consultantSpecialtyRef.current,
      );
      setStoredActiveSession(authProfile?.tenant_id, newSessionId);
      pretextLoadSeqRef.current += 1;
      resetConversation(
        consultantMode,
        consultantSpecialtyRef.current,
        newSessionId,
      );
      setShowHistory(false);
      await saveCurrentConversation();
      await loadChatHistory();
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorDeleteConversation'));
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportStartedAt(Date.now());
    setReportGenFailed(false);
    setReportGenWarnings([]);
    setError(null);
    setReportProgressStep('Starting report generation…');
    setReportProgressPercent(5);
    setReportProgressDetail('Starting');
    setReportProgressSteps(buildReportProgressSteps('starting'));
    goToSection(reportSectionRef);

    const abortController = beginCancellableJob('report_generate', {
      sessionId: sessionIdRef.current,
    });

    const applyProgressStatus = (status) => {
      const formatted = formatReportProgressStatus(status);
      if (!formatted) return;
      setReportProgressStep(formatted.label);
      setReportProgressDetail(formatted.detail);
      setReportProgressSteps(buildReportProgressSteps(formatted.phase));
      if (typeof formatted.progress === 'number') setReportProgressPercent(formatted.progress);
      if (formatted.warnings.length > 0) {
        setReportGenWarnings(formatted.warnings);
      }
    };

    const pollReportStatus = async () => {
      const params = new URLSearchParams({
        session_id: sessionIdRef.current,
        _t: String(Date.now()),
      });
      const response = await apiFetch(
        `/api/reports/generate/status?${params.toString()}`,
        { signal: abortController.signal, cache: 'no-store' },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const gatewayFallback = [502, 503, 504].includes(response.status)
          ? 'API server is unavailable. The backend may need to be restarted.'
          : 'Failed to check report status';
        throw new Error(formatApiErrorDetail(errorData.detail, gatewayFallback));
      }
      return response.json();
    };

    const waitForReportCompletion = async () => {
      const deadline = Date.now() + 45 * 60 * 1000;
      while (!abortController.signal.aborted) {
        const status = await pollReportStatus();
        applyProgressStatus(status);
        if (status.status === 'completed') {
          if (!status.report) {
            throw new Error('Report generation finished but no report payload was returned.');
          }
          return status;
        }
        if (status.status === 'failed') {
          throw new Error(
            formatApiErrorDetail(
              {
                message: status.message,
                error: status.error,
                debug: { error: status.error, phase: status.phase },
              },
              'Failed to generate report',
            ),
          );
        }
        if (status.status === 'cancelled') {
          throw new Error(status.message || 'Report generation was cancelled.');
        }
        if (Date.now() >= deadline) {
          throw new Error('Report generation timed out after 45 minutes. Please try again.');
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      throw new Error('Report generation was cancelled.');
    };

    const pollTimer = setInterval(async () => {
      if (abortController.signal.aborted) return;
      try {
        applyProgressStatus(await pollReportStatus());
      } catch (pollErr) {
        if (pollErr.name !== 'AbortError') {
          console.warn('Report progress poll failed:', pollErr);
        }
      }
    }, 2000);

    try {
      await saveEngagement();
      await saveCurrentConversation({
        reportInstructions,
        reportPromptId,
        reportPromptName,
      });
      const response = await apiFetch('/api/reports/generate', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          mode: consultantModeRef.current,
          specialty: consultantSpecialtyRef.current,
          report_template_id: reportTemplateId,
          report_language: resolveLanguageValue(reportLanguage, customReportLanguage),
          export_language_mode: exportLanguageMode,
        }),
      });

      if (!response.ok && response.status !== 202) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiErrorDetail(errorData.detail, 'Failed to generate report'),
        );
      }

      applyProgressStatus(await pollReportStatus());
      const data = await waitForReportCompletion();
      const reportPayload = data.report;
      const baselinePayload = data.report_baseline ?? null;
      const sourceLanguage = data.report_source_language ?? '';
      const targetLanguage = data.report_target_language ?? '';
      setReport(reportPayload);
      setExportReport(reportPayload);
      setReportBaseline(exportLanguageMode === 'side_by_side' ? baselinePayload : null);
      setReportReviewMode(
        exportLanguageMode === 'side_by_side' && baselinePayload
          ? 'side_by_side'
          : 'translated',
      );
      setReportSourceLanguage(exportLanguageMode === 'side_by_side' ? sourceLanguage : '');
      setReportTargetLanguage(exportLanguageMode === 'target_only' ? targetLanguage : '');
      setShowReport(true);
      setReportGenFailed(false);
      await loadPretexts();

      await saveCurrentConversation({
        report: reportPayload,
        reportBaseline: exportLanguageMode === 'side_by_side' ? baselinePayload : null,
        reportReviewMode:
          exportLanguageMode === 'side_by_side' && baselinePayload
            ? 'side_by_side'
            : 'translated',
        reportSourceLanguage: exportLanguageMode === 'side_by_side' ? sourceLanguage : '',
        reportTargetLanguage: exportLanguageMode === 'target_only' ? targetLanguage : '',
        exportLanguageMode,
      });
      setTimeout(() => goToSection(reportSectionRef), 150);

      const reportMessage = {
        role: 'assistant',
        content: `I've generated a comprehensive report titled "${reportPayload.title}" with all consultant findings, developed pretexts/scripts, and topic-by-topic analysis. You can review it below and download it as DOCX or PDF.`,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, reportMessage]);
    } catch (err) {
      const cancelled = isJobCancelledError(err);
      setReportGenFailed(true);
      setError(
        cancelled
          ? 'Report generation was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorGenerateReport')),
      );
      if (!cancelled) {
        await loadPretexts();
      }
    } finally {
      clearInterval(pollTimer);
      endCancellableJob();
      setReportProgressStep('');
      setReportProgressPercent(null);
      setReportProgressDetail('');
      setReportProgressSteps([]);
      setReportStartedAt(null);
      setGeneratingReport(false);
    }
  };

  const handleTranslateChat = async () => {
    const targetLanguage = pickTranslationTarget(
      reportLanguage,
      customReportLanguage,
      chatLanguage,
      customChatLanguage,
    );
    if (!targetLanguage) {
      setError(tUi(uiLanguage, 'appErrorChooseLanguageBeforeTranslate'));
      goToSection(engagementRef, { openTools: true, panelId: 'engagement' });
      return;
    }

    goToSection(chatSectionRef);
    setTranslatingChat(true);
    setError(null);
    const abortController = beginCancellableJob('chat_translate', {
      sessionId: sessionIdRef.current,
    });
    try {
      const chatBaselineSnapshot = JSON.parse(JSON.stringify(messages));
      setMessagesBaseline(chatBaselineSnapshot);
      await saveEngagement();
      const response = await apiFetch('/api/chat/translate', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          target_language: targetLanguage,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorTranslateChat'));
      }
      const data = await response.json();
      setMessages(data.messages || []);
      setChatReviewMode('side_by_side');
      await saveCurrentConversation({
        messages: data.messages || [],
        messagesBaseline: chatBaselineSnapshot,
        chatReviewMode: 'side_by_side',
      });
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Chat translation was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorTranslateChat')),
      );
    } finally {
      endCancellableJob();
      setTranslatingChat(false);
    }
  };

  const handleTranslateReport = async () => {
    const payload = deliverableExportReport;
    if (!payload) {
      setError(tUi(uiLanguage, 'appErrorGenerateReportBeforeTranslate'));
      goToSection(reportSectionRef);
      return;
    }

    const targetLanguage = pickTranslationTarget(
      reportLanguage,
      customReportLanguage,
      chatLanguage,
      customChatLanguage,
    );
    if (!targetLanguage) {
      setError(tUi(uiLanguage, 'appErrorChooseLanguageBeforeTranslateReport'));
      goToSection(engagementRef, { openTools: true, panelId: 'engagement' });
      return;
    }

    goToSection(reportSectionRef);
    setTranslatingReport(true);
    setError(null);
    const abortController = beginCancellableJob('report_translate');
    const sourceSnapshot = JSON.parse(JSON.stringify(payload));
    const sourceLanguage = resolveReportSourceLanguage(
      reportLanguage,
      customReportLanguage,
      analystLanguage,
      customAnalystLanguage,
    );
    try {
      const response = await apiFetch('/api/reports/translate', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          report: payload,
          target_language: targetLanguage,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorTranslateReport'));
      }
      const data = await response.json();
      const reportPayload = data.report ?? data;
      const translationComplete = data.translation_complete ?? true;
      const untranslatedFields = data.untranslated_fields ?? [];
      const validation = validateReportTranslation(sourceSnapshot, reportPayload, targetLanguage);

      if (!translationComplete || !validation.ok) {
        const warningParts = [...validation.warnings];
        if (untranslatedFields.length) {
          warningParts.push(`Untranslated fields: ${untranslatedFields.join(', ')}`);
        }
        showActionFeedback(
          warningParts.join(' ')
            || tUi(uiLanguage, 'appTranslationIncompleteWarning'),
          'warning',
        );
      }

      setReportBaseline(sourceSnapshot);
      setReportSourceLanguage(sourceLanguage);
      setReportTargetLanguage(targetLanguage);
      setReportReviewMode('side_by_side');
      setExportLanguageMode('target_only');
      setReport(reportPayload);
      setExportReport(reportPayload);
      setShowReport(true);
      await saveCurrentConversation({
        report: reportPayload,
        reportBaseline: sourceSnapshot,
        reportReviewMode: 'side_by_side',
        reportSourceLanguage: sourceLanguage,
        reportTargetLanguage: targetLanguage,
        exportLanguageMode: 'target_only',
      });
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Report translation was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorTranslateReport')),
      );
    } finally {
      endCancellableJob();
      setTranslatingReport(false);
    }
  };

  const handleReportQa = async () => {
    const payload = deliverableExportReport;
    if (!payload) {
      setError(tUi(uiLanguage, 'appErrorGenerateReportBeforeQa'));
      goToSection(reportSectionRef);
      return;
    }

    const targetLanguage = pickTranslationTarget(
      reportLanguage,
      customReportLanguage,
      chatLanguage,
      customChatLanguage,
    );
    if (!targetLanguage) {
      setError(tUi(uiLanguage, 'appErrorChooseLanguageBeforeQa'));
      goToSection(engagementRef, { openTools: true, panelId: 'engagement' });
      return;
    }

    goToSection(reportSectionRef);
    setRunningReportQa(true);
    setError(null);
    const abortController = beginCancellableJob('report_qa', {
      sessionId: sessionIdRef.current,
    });
    try {
      const response = await apiFetch('/api/reports/qa', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          report: payload,
          target_language: targetLanguage,
          session_id: sessionIdRef.current,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorRunTranslationQa'));
      }
      setReportQa(await response.json());
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'Translation QA was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorRunTranslationQa')),
      );
    } finally {
      endCancellableJob();
      setRunningReportQa(false);
    }
  };

  const handleReportExcludedSectionsChange = React.useCallback(async (nextExcluded) => {
    setReportExcludedSections(nextExcluded);
    try {
      await saveCurrentConversation({ reportExcludedSections: nextExcluded });
      await saveEngagement();
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorSaveReportSectionPrefs'));
    }
  }, [saveCurrentConversation, saveEngagement]);

  const handleReportExcludedSectionKeysChange = React.useCallback(async (nextExcludedKeys) => {
    setReportExcludedSectionKeys(nextExcludedKeys);
    try {
      await saveCurrentConversation({ reportExcludedSectionKeys: nextExcludedKeys });
    } catch (err) {
      setError(err.message || 'Failed to save per-section report include preferences.');
    }
  }, [saveCurrentConversation]);

  const handleDownloadReport = async (format) => {
    const payload = deliverableExportReport;
    if (!payload) {
      setError(tUi(uiLanguage, 'appErrorGenerateReportBeforeDownload'));
      goToSection(reportSectionRef);
      return;
    }
    if (!hasExportableReportContent(payload)) {
      setError('No report sections are selected for export. Select at least one section to continue.');
      goToSection(reportSectionRef);
      return;
    }

    if (exportLanguageMode === 'side_by_side' && !reportBaseline) {
      const targetLanguage = pickTranslationTarget(
        reportLanguage,
        customReportLanguage,
        chatLanguage,
        customChatLanguage,
      );
      if (!targetLanguage) {
        setError(tUi(uiLanguage, 'appErrorChooseLanguageBeforeBilingualExport'));
        goToSection(engagementRef, { openTools: true, panelId: 'engagement' });
        return;
      }
    }

    setError(null);
    goToSection(reportSectionRef);
    setDownloadingReport(format);
    const abortController = beginCancellableJob('report_export', {
      sessionId: sessionIdRef.current,
    });

    try {
      const response = await apiFetch('/api/reports/export', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          report: payload,
          format,
          session_id: sessionIdRef.current,
          export_language_mode: exportLanguageMode,
          report_baseline: deliverableReportBaseline || undefined,
          report_source_language: reportSourceLanguage || resolveReportSourceLanguage(
            reportLanguage,
            customReportLanguage,
            analystLanguage,
            customAnalystLanguage,
          ),
          report_target_language: reportTargetLanguage || pickTranslationTarget(
            reportLanguage,
            customReportLanguage,
            chatLanguage,
            customChatLanguage,
          ),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail
          || tUi(uiLanguage, 'appErrorExportFormat', { format: format.toUpperCase() }),
        );
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const templateLabel = reportTemplates.find((item) => item.id === reportTemplateId)?.label
        || 'Report';
      const filename = parseContentDispositionFilename(disposition)
        || buildExportFilename({
          clientName,
          projectName,
          reportTemplateLabel: templateLabel,
          classification,
          customClassification,
          extension: format,
          reportPayload: payload,
        });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setPreferredExportFormat(format);
      await saveEngagement();
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? `${format.toUpperCase()} download was cancelled. You can try again.`
          : (
            err.message
            || tUi(uiLanguage, 'appErrorDownloadReport', { format: format.toUpperCase() })
          ),
      );
    } finally {
      endCancellableJob();
      setDownloadingReport(null);
    }
  };

  const rememberPdfFiles = (files) => {
    files.forEach((file) => {
      if (file.name?.toLowerCase().endsWith('.pdf')) {
        pendingPdfFilesRef.current.set(file.name, file);
      }
    });
  };

  const uploadFilesToServer = async (files, pdfPasswords = {}, signal = undefined) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('session_id', sessionIdRef.current);
    if (Object.keys(pdfPasswords).length > 0) {
      formData.append('pdf_passwords', JSON.stringify(pdfPasswords));
    }

    const response = await apiFetch('/api/upload_file', {
      method: 'POST',
      body: formData,
      headers: getApiHeaders(false),
      signal,
    });

    if (!response.ok) {
      let errorMessage = tUi(uiLanguage, 'appErrorUploadFiles');
      try {
        const errorData = await response.json();
        errorMessage = formatUploadErrorMessage(response, errorData, uiLanguage);
      } catch (parseErr) {
        errorMessage = formatUploadErrorMessage(response, {}, uiLanguage);
      }
      throw new Error(errorMessage);
    }

    return response.json();
  };

  const applySuccessfulUploads = async (data) => {
    const fileNeedsPassword = (file) => file.needs_password
      || file.password_invalid
      || /password-protected|enter the document password|incorrect password/i.test(file.content_summary || '');

    const successfulFiles = (data.files || []).filter((file) => !fileNeedsPassword(file));
    if (successfulFiles.length === 0) {
      return null;
    }

    const newFiles = successfulFiles.map((file) => ({
      filename: file.filename,
      file_type: file.file_type,
      summary: file.content_summary,
    }));

    const updatedUploadedFiles = [...uploadedFiles, ...newFiles];
    setUploadedFiles(updatedUploadedFiles);

    const extractionWarnings = newFiles
      .filter((file) =>
        /no extractable text|could not be read|ocr failed|no readable preview/i.test(file.summary || ''),
      )
      .map((file) => file.filename);

    const uploadMessage = {
      role: 'assistant',
      content: extractionWarnings.length > 0
        ? `${data.message}\n\nNote: ${extractionWarnings.join(', ')} could not be fully read. The consultant may ask you to re-upload or provide a text-based version.`
        : data.message,
      timestamp: new Date().toISOString(),
    };
    const updatedMessages = [...messages, uploadMessage];
    setMessages(updatedMessages);

    saveCurrentConversation({
      messages: updatedMessages,
      uploadedFiles: updatedUploadedFiles,
    }).catch((saveErr) => {
      console.error('Failed to save conversation after upload:', saveErr);
    });

    successfulFiles.forEach((file) => {
      pendingPdfFilesRef.current.delete(file.filename);
    });

    return data;
  };

  const openNextPdfPasswordPrompt = (pendingItems) => {
    if (!pendingItems.length) {
      setPdfPasswordPrompt(null);
      return;
    }

    const total = pendingItems.length;
    for (let index = 0; index < total; index += 1) {
      const item = pendingItems[index];
      const file = pendingPdfFilesRef.current.get(item.filename);
      if (file) {
        setPdfPasswordPrompt({
          filename: item.filename,
          file,
          passwordInvalid: Boolean(item.password_invalid),
          queuePosition: { current: index + 1, total },
        });
        return;
      }
    }
    setPdfPasswordPrompt(null);
    setError('Password-protected PDF could not be retried. Please upload the file again.');
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    rememberPdfFiles(files);
    goToSection(chatSectionRef);
    setUploadingFile(true);
    setError(null);
    const abortController = beginCancellableJob('file_upload', {
      sessionId: sessionIdRef.current,
    });

    try {
      const data = await uploadFilesToServer(files, {}, abortController.signal);
      await applySuccessfulUploads(data);

      const pendingPassword = (data.files || []).filter(
        (file) => file.needs_password
          || file.password_invalid
          || /password-protected|enter the document password|incorrect password/i.test(file.content_summary || ''),
      );
      if (pendingPassword.length > 0) {
        setPdfPasswordQueue(pendingPassword);
        openNextPdfPasswordPrompt(pendingPassword);
      } else {
        setPdfPasswordQueue([]);
      }
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'File upload was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorUploadFiles')),
      );
    } finally {
      endCancellableJob();
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePdfPasswordSubmit = async (password) => {
    if (!pdfPasswordPrompt?.file) return;

    setUploadingFile(true);
    setError(null);
    const abortController = beginCancellableJob('file_upload', {
      sessionId: sessionIdRef.current,
    });

    try {
      const { filename, file } = pdfPasswordPrompt;
      const data = await uploadFilesToServer([file], { [filename]: password }, abortController.signal);
      await applySuccessfulUploads(data);

      const stillPending = (data.files || []).filter(
        (file) => file.needs_password
          || file.password_invalid
          || /password-protected|enter the document password|incorrect password/i.test(file.content_summary || ''),
      );
      const unlocked = !stillPending.some((file) => file.filename === filename);

      setPdfPasswordQueue((prev) => {
        let remaining = prev;
        if (unlocked) {
          remaining = prev.filter((item) => item.filename !== filename);
        } else {
          remaining = prev.map((item) => (
            item.filename === filename
              ? { ...item, password_invalid: true }
              : item
          ));
        }
        if (remaining.length > 0) {
          openNextPdfPasswordPrompt(remaining);
        } else {
          setPdfPasswordPrompt(null);
        }
        return remaining;
      });
    } catch (err) {
      setError(
        isJobCancelledError(err)
          ? 'File upload was cancelled. You can try again.'
          : (err.message || tUi(uiLanguage, 'appErrorUnlockPdf')),
      );
    } finally {
      endCancellableJob();
      setUploadingFile(false);
    }
  };

  const handlePdfPasswordCancel = () => {
    if (pdfPasswordPrompt?.filename) {
      pendingPdfFilesRef.current.delete(pdfPasswordPrompt.filename);
    }
    setPdfPasswordQueue((prev) => {
      const remaining = prev.filter((item) => item.filename !== pdfPasswordPrompt?.filename);
      if (remaining.length > 0) {
        openNextPdfPasswordPrompt(remaining);
      } else {
        setPdfPasswordPrompt(null);
      }
      return remaining;
    });
  };

  const getFollowUpExcerpt = (messageIndex) => (
    followUpSelection?.messageIndex === messageIndex ? followUpSelection.excerpt : null
  );

  const handleAssistantTextSelect = (messageIndex, containerEl) => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (!text || text.length < 2) return;
    if (!containerEl?.contains(selection.anchorNode)) return;
    setFollowUpSelection({ messageIndex, excerpt: text });
  };

  const handleFollowUpSubmit = ({ question, excerpt, answerContent }) => {
    if (!question?.trim() || !answerContent?.trim() || loading || isProcessingRef.current) {
      return;
    }
    submitVoiceMessage(question.trim(), {
      intent: 'followup',
      referenceAnswer: answerContent,
      referenceExcerpt: excerpt?.trim() || undefined,
    });
    setFollowUpSelection(null);
  };

  // Submit voice message function (shared between auto-submit and manual)
  const submitVoiceMessage = async (transcript, options = {}) => {
    if (!transcript || !transcript.trim()) {
      console.log('SubmitVoiceMessage: Empty transcript, skipping');
      return;
    }
    
    if (isProcessingRef.current) {
      console.log('SubmitVoiceMessage: Already processing, skipping');
      return;
    }
    
    console.log('SubmitVoiceMessage: Submitting:', transcript.trim());
    
    // Clear any pending timeout
    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }
    
    if (
      EXERCISE_CONSULTANT_MODES.has(consultantModeRef.current)
      && !nationStateConsentRef.current
      && !messages.some((message) => message.role === 'user')
    ) {
      setError('Nation State Consultant mode requires authorized-use consent before chatting. Enter your name above and click "I Confirm Authorized Use".');
      goToSection(consentRef, { openTools: true });
      return;
    }

    isProcessingRef.current = true;
    const requestSessionId = sessionIdRef.current;
    const isStaleChatRequest = () => requestSessionId !== sessionIdRef.current;
    goToSection(chatSectionRef);
    const messageText = transcript.trim();
    const excerpt = options.referenceExcerpt?.trim();
    const userDisplayContent = excerpt
      ? `About: "${excerpt.length > 280 ? `${excerpt.slice(0, 280)}…` : excerpt}"\n\n${messageText}`
      : messageText;

    const userMessage = {
      role: 'user',
      content: userDisplayContent,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setAppliedMandateText('');
    if (mandateApplyRef.current.sessionId === sessionIdRef.current) {
      mandateApplyRef.current = { ...mandateApplyRef.current, text: '' };
    }
    setLoading(true);
    setChatJobStartedAt(Date.now());
    setError(null);
    setPolicyRefusalHint(false);
    lastTranscriptRef.current = ''; // Clear so we can detect new speech
    const abortController = beginCancellableJob('chat', {
      sessionId: sessionIdRef.current,
    });

    const streamingAssistant = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      citations: [],
      groundingCount: 0,
      filesInContext: 0,
      isStreaming: true,
    };
    setMessages(prev => [...prev, streamingAssistant]);

    try {
      // Build conversation history for API
      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));
      
      const response = await apiFetch('/api/chat', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          message: messageText,
          conversation_history: conversationHistory.slice(0, -1),
          mode: consultantModeRef.current,
          specialty: consultantSpecialtyRef.current,
          chat_language: resolveLanguageValue(chatLanguage, customChatLanguage),
          intent: options.intent || undefined,
          reference_answer: options.referenceAnswer || undefined,
          reference_excerpt: options.referenceExcerpt || undefined,
          operational_mandates: consultantModeRef.current === 'nation_state' ? operationalMandates : undefined,
          tradecraft_source_ids: consultantModeRef.current === 'nation_state' ? tradecraftSourceIds : undefined,
          wraith_generation_mode: consultantModeRef.current === 'nation_state' ? wraithGenerationMode : undefined,
          stream: true,
        }),
      });
      
      if (!response.ok && response.status !== 202) {
        const errorData = await parseJsonResponse(response);
        const isHtml = (response.headers.get('content-type') || '').includes('text/html');
        throw new Error(
          errorData.detail
            || (isHtml ? 'The server returned an error page (timeout or gateway error). Please try again.' : tUi(uiLanguage, 'appErrorGetResponse')),
        );
      }

      const contentType = response.headers.get('content-type') || '';
      let data;
      if (response.status === 202) {
        data = await waitForChatJobCompletion(sessionIdRef.current, {
          signal: abortController.signal,
          onStreamedText: (text) => {
            streamingAssistant.content = text;
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...streamingAssistant };
              return next;
            });
          },
        });
      } else if (contentType.includes('text/event-stream')) {
        data = await consumeChatStream(response, {
          signal: abortController.signal,
          onToken: (token) => {
            streamingAssistant.content += token;
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1] = { ...streamingAssistant };
              return next;
            });
          },
          onReplace: (content) => {
            streamingAssistant.content = content;
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1] = { ...streamingAssistant };
              return next;
            });
          },
        });
      } else {
        data = await parseJsonResponse(response);
      }

      let responseText = (data.message || '').trim();
      if (!responseText || isDegenerateAssistantContent(responseText)) {
        throw new Error(
          responseText && isDegenerateAssistantContent(responseText)
            ? 'The consultant returned an empty or incomplete response. Please try again.'
            : tUi(uiLanguage, 'appErrorGetResponse'),
        );
      }
      if (data.chat_language) {
        const nextChatLanguage = hydrateLanguageSelect(data.chat_language);
        setChatLanguage(nextChatLanguage.select);
        setCustomChatLanguage(nextChatLanguage.custom);
      }
      
      // Add assistant response metadata
      const assistantMessage = {
        role: 'assistant',
        content: responseText,
        timestamp: new Date().toISOString(),
        citations: data.citations || [],
        groundingCount: data.grounding_count ?? 0,
        filesInContext: data.files_in_context ?? 0,
        isStreaming: false,
      };

      let messagesToSave;
      setMessages((prev) => {
        const withoutStreamingPlaceholder = (
          prev.length > 0
          && prev[prev.length - 1].role === 'assistant'
          && !prev[prev.length - 1].content
            ? prev.slice(0, -1)
            : prev
        );
        const last = withoutStreamingPlaceholder[withoutStreamingPlaceholder.length - 1];
        const hasUserTurn = last?.role === 'user' && last.content === userMessage.content;
        const base = hasUserTurn ? withoutStreamingPlaceholder : [...withoutStreamingPlaceholder, userMessage];
        messagesToSave = [...base, assistantMessage];
        return messagesToSave;
      });

      if (isStaleChatRequest()) {
        return;
      }

      setPolicyRefusalHint(Boolean(data.policy_refusal_detected));

      if (data.ask_about_services) {
        setAskAboutServices(true);
      }

      try {
        await saveCurrentConversation({
          messages: messagesToSave,
        });
      } catch (saveErr) {
        console.error('Failed to save conversation:', saveErr);
      }

      if (isStaleChatRequest()) {
        return;
      }

      if (
        data.files_in_context === 0 &&
        uploadedFiles.length > 0 &&
        /pdf|upload|file|document|profile/i.test(messageText)
      ) {
        setError('Your uploaded files were not loaded into this chat session. Please upload again, then resend your message.');
      }
      
      // Automatically speak the response if voice is enabled
      // Start TTS immediately (no delay) - React will update UI synchronously
      if (voiceEnabled && !data.policy_refusal_detected && !isStaleChatRequest()) {
        // Set speaking state immediately to show feedback while generating audio
        setIsSpeaking(true);
        // Start TTS request immediately - ElevenLabs will take a few seconds to generate
        // but we want to start the request as soon as possible
        speakText(responseText).then(() => {
          // After speaking finishes, restart listening immediately if still in conversation mode
          // Use ref to check conversation mode (more reliable than state)
          if (conversationModeRef.current && !userExitedConversationModeRef.current && recognitionRef.current) {
            try {
              // Always try to start - recognition might have stopped
              console.log('🔄 Attempting to restart listening after speech...');
              recognitionRef.current.start();
              console.log('✅ Restarted listening immediately after speech');
            } catch (err) {
              // If already started, that's fine
              if (err.message && err.message.includes('already started')) {
                console.log('✅ Recognition already started');
              } else {
                // Try again after short delay
                console.log('⚠️ First restart attempt failed, retrying...', err.message);
                setTimeout(() => {
                  try {
                    if (conversationModeRef.current && !userExitedConversationModeRef.current && recognitionRef.current) {
                      recognitionRef.current.start();
                      console.log('✅ Restarted listening on retry');
                    }
                  } catch (retryErr) {
                    console.log('❌ Mic restart after speech error:', retryErr.message);
                  }
                }, 500);
              }
            }
          } else {
            console.log('⚠️ Not restarting - conversationMode:', conversationModeRef.current, 'recognition:', !!recognitionRef.current);
          }
        });
      } else {
        // Even without voice, restart listening if in conversation mode
        if (conversationModeRef.current && !userExitedConversationModeRef.current && recognitionRef.current) {
          setTimeout(() => {
            try {
              if (conversationModeRef.current && !userExitedConversationModeRef.current) {
                recognitionRef.current.start();
                console.log('✅ Restarted listening (voice disabled)');
              }
            } catch (err) {
              if (err.message && err.message.includes('already started')) {
                console.log('✅ Recognition already started');
              } else {
                console.log('Mic restart error:', err.message);
              }
            }
          }, 500);
        }
      }
      
      // Check if we should ask about services
      if (data.ask_about_services && CONSULTANT_MODES[consultantModeRef.current]?.supportsServices) {
        setAskAboutServices(true);
      }
      
    } catch (err) {
      if (isStaleChatRequest()) {
        return;
      }
      if (isJobCancelledError(err)) {
        setMessages(prev => (
          prev.length > 0 && prev[prev.length - 1].role === 'assistant' && !prev[prev.length - 1].content
            ? prev.slice(0, -1)
            : prev
        ));
        setError('Consultant response was cancelled. You can send your message again.');
      } else {
        setError(err.message || 'An error occurred');
        const errorMessage = {
          role: 'assistant',
          content: `I apologize, but I encountered an error: ${err.message}. Please try again.`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => {
          const base = (
            prev.length > 0 && prev[prev.length - 1].role === 'assistant' && !prev[prev.length - 1].content
              ? prev.slice(0, -1)
              : prev
          );
          return [...base, errorMessage];
        });

        if (conversationModeRef.current && !userExitedConversationModeRef.current && recognitionRef.current) {
          setTimeout(() => {
            if (!conversationModeRef.current || userExitedConversationModeRef.current) {
              return;
            }
            try {
              recognitionRef.current.start();
            } catch (restartErr) {
              console.log('Could not restart mic:', restartErr);
            }
          }, 1000);
        }
      }
    } finally {
      if (!isStaleChatRequest()) {
        endCancellableJob();
        setLoading(false);
        setChatJobStartedAt(null);
        isProcessingRef.current = false;
      }
    }
  };

  const sendAppliedSessionPrompt = async (text) => {
    const draft = (text || appliedMandateText || effectiveChatDraft || '').trim();
    if (!draft || loading || isProcessingRef.current) {
      return;
    }
    goToSection(chatSectionRef);
    await submitVoiceMessage(draft);
  };

  // Voice input handlers
  const startListening = React.useCallback(() => {
    if (recognitionRef.current) {
      try {
        userExitedConversationModeRef.current = false;
        setConversationMode(true); // Enable conversation mode
        conversationModeRef.current = true; // Update ref immediately
        recognitionRef.current.continuous = true;
        lastTranscriptRef.current = ''; // Reset transcript tracking
        
        // Speak welcome message first, then start listening
        if (voiceEnabled) {
          const welcomeText = getWelcomeMessage(
            consultantModeRef.current,
            activeUserLanguage(chatLanguage, customChatLanguage),
          );
          
          // Ensure we use the selected voice (wait a moment if voice isn't loaded yet)
          const speakWelcome = () => {
            console.log('Speaking welcome message with voice:', selectedVoiceId || 'default');
            speakText(welcomeText).then(() => {
              // After welcome message finishes, start listening
              console.log('Welcome message finished, starting listening...');
              setTimeout(() => {
                try {
                  if (recognitionRef.current && conversationModeRef.current && !userExitedConversationModeRef.current) {
                    recognitionRef.current.start();
                    console.log('✅ Started listening after welcome message');
                  } else {
                    console.log('⚠️ Not in conversation mode or recognition not available');
                  }
                } catch (err) {
                  // If already started, that's fine
                  if (err.message && err.message.includes('already started')) {
                    console.log('✅ Recognition already started');
                  } else {
                    console.log('Error starting after welcome:', err);
                  }
                }
              }, 300);
            });
          };
          
          // If ElevenLabs is available but voice isn't selected yet, wait a moment
          if (elevenLabsAvailable && !selectedVoiceId) {
            console.log('Waiting for voice selection...');
            setTimeout(() => {
              speakWelcome();
            }, 500);
          } else {
            speakWelcome();
          }
        } else {
          // Start listening immediately if voice is disabled
          if (!isListening) {
            recognitionRef.current.start();
            console.log('Started listening immediately (voice disabled)');
          }
        }
      } catch (err) {
        console.error('Error starting speech recognition:', err);
        if (err.message && !err.message.includes('already started')) {
          setError('Could not start voice input. Please try again.');
        }
      }
    }
  }, [voiceEnabled, conversationMode, isListening, elevenLabsAvailable, selectedVoiceId, speakText]);

  const stopListening = React.useCallback(() => {
    const shouldSubmitPending =
      effectiveChatDraft.trim() && !isProcessingRef.current && conversationMode;

    userExitedConversationModeRef.current = true;
    conversationModeRef.current = false;
    setConversationMode(false);

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    if (shouldSubmitPending) {
      submitVoiceMessage(effectiveChatDraft);
    }

    stopSpeaking();
    lastTranscriptRef.current = '';

    if (recognitionRef.current) {
      recognitionRef.current.continuous = false;
      try {
        recognitionRef.current.stop();
      } catch (err) {
        console.log('Recognition stop:', err.message);
      }
      if (typeof recognitionRef.current.abort === 'function') {
        try {
          recognitionRef.current.abort();
        } catch (err) {
          console.log('Recognition abort:', err.message);
        }
      }
    }

    setIsListening(false);
  }, [conversationMode, effectiveChatDraft, stopSpeaking, submitVoiceMessage]);
  
  const toggleConversationMode = () => {
    if (conversationMode && isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const toggleVoice = () => {
    setVoiceEnabled(prev => {
      if (prev) {
        // If disabling, stop any ongoing speech
        stopSpeaking();
      }
      return !prev;
    });
  };

  const handleServiceInquiry = async (email, wantsServices) => {
    try {
      setLoading(true);
      setError(null);

      // Build conversation summary
      const conversationText = messages.map(msg => `${msg.role}: ${msg.content}`).join('\n');

      const response = await apiFetch('/api/service_inquiry', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          email: email,
          wants_services: wantsServices,
          conversation_summary: conversationText
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || tUi(uiLanguage, 'appErrorSubmitInquiry'));
      }

      const data = await response.json();
      
      // Add confirmation message
      const confirmationMessage = {
        role: 'assistant',
        content: data.message,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, confirmationMessage]);
      setShowServiceForm(false);
      setAskAboutServices(false);

    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'appErrorSubmitServiceInquiry'));
    } finally {
      setLoading(false);
    }
  };

  const openAdminPortal = () => {
    setStoredAdminView({ open: true, tab: getStoredAdminView().tab || 'organizations' });
    setShowAdminPortal(true);
  };

  const tier1ScreenLanguage = useMemo(() => {
    const value = activeUserLanguage(chatLanguage, customChatLanguage);
    return value === 'auto' ? 'en' : value;
  }, [chatLanguage, customChatLanguage]);

  const tier1ReportLanguage = useMemo(() => {
    const value = resolveLanguageValue(reportLanguage, customReportLanguage);
    return value === 'auto' ? 'en' : value;
  }, [reportLanguage, customReportLanguage]);

  const handleTier1ScreenLanguageChange = React.useCallback((code) => {
    setChatLanguage(code);
    setCustomChatLanguage('');
  }, []);

  const handleTier1ReportLanguageChange = React.useCallback((code) => {
    setReportLanguage(code);
    setCustomReportLanguage('');
  }, []);

  const saveTier1Languages = React.useCallback(async () => {
    if (!sessionIdRef.current) return;
    const response = await apiFetch('/api/engagements', {
      method: 'PUT',
      body: JSON.stringify({
        session_id: sessionIdRef.current,
        chat_language: tier1ScreenLanguage,
        report_language: tier1ReportLanguage,
        mode: consultantModeRef.current,
        specialty: consultantSpecialtyRef.current,
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to save language settings.');
    }
  }, [tier1ScreenLanguage, tier1ReportLanguage]);

  const handleTier1ReportHtmlUpdated = React.useCallback(({ html, title, cleared, report: reportPayload, reportSourceLanguage, reportTargetLanguage }) => {
    if (cleared || !html) {
      setReport(null);
      setExportReport(null);
      saveCurrentConversation({ report: null }).catch((err) => {
        console.warn('Failed to clear Tier 1 report:', err);
      });
      return;
    }
    const nextReport = reportPayload
      ? { ...reportPayload, html_content: html || reportPayload.html_content }
      : {
          ...(report || {}),
          html_content: html,
          title: title || report?.title,
          report_type: 'tier1_html',
        };
    if (title) {
      nextReport.title = title;
    }
    setReport(nextReport);
    setExportReport(nextReport);
    if (reportSourceLanguage) {
      setReportSourceLanguage(reportSourceLanguage);
    }
    if (reportTargetLanguage) {
      setReportTargetLanguage(reportTargetLanguage);
      setReportLanguage(reportTargetLanguage);
      setCustomReportLanguage('');
    }
    saveCurrentConversation({
      report: nextReport,
      title: title || conversationTitle,
      reportSourceLanguage,
      reportTargetLanguage,
    }).catch((err) => {
      console.warn('Failed to save updated Tier 1 report:', err);
    });
  }, [report, conversationTitle, saveCurrentConversation]);

  const handleTier1UploadedFilesChange = React.useCallback((files) => {
    setUploadedFiles(files);
    saveCurrentConversation({ uploadedFiles: files }).catch((err) => {
      console.warn('Failed to save uploaded files for Tier 1 session:', err);
    });
  }, [saveCurrentConversation]);

  const handleTier1DraftChange = React.useCallback((draft) => {
    const normalized = normalizeTier1Draft(draft);
    if (tier1DraftKey(normalized) === tier1DraftKey(tier1Draft)) {
      return;
    }
    setTier1Draft(normalized);
    saveCurrentConversation({ tier1Draft: normalized }).catch((err) => {
      console.warn('Failed to save Tier 1 workspace draft:', err);
    });
  }, [tier1Draft, saveCurrentConversation]);

  const tier1ReportChatMessages = useMemo(
    () => tier1ReportChatMessagesFromConversation(messages),
    [messages],
  );

  const handleTier1ReportChatMessagesChange = React.useCallback((reportChatMessages) => {
    setMessages((prev) => {
      const merged = mergeTier1ReportChatIntoConversation(prev, reportChatMessages);
      saveCurrentConversation({ messages: merged }).catch((err) => {
        console.warn('Failed to save Tier 1 report chat messages:', err);
      });
      return merged;
    });
  }, [saveCurrentConversation]);

  const handleTier1ConversationRefresh = React.useCallback(async () => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) return;
    try {
      await loadConversationById(currentSessionId);
    } catch (err) {
      console.warn('Failed to refresh Tier 1 conversation after report chat:', err);
    }
  }, [loadConversationById]);

  const closeAdminPortal = () => {
    clearStoredAdminView();
    setShowAdminPortal(false);
  };

  if (showAdminPortal && isSuperadminRole(authProfile?.role)) {
    return (
      <AdminPortal
        authProfile={authProfile}
        onBack={closeAdminPortal}
      />
    );
  }

  if (consultantMode === 'nation_state') {
    return (
      <>
        <SimplifiedTier1App
          authProfile={authProfile}
          onLogout={handleSignOut}
          uiLanguage={uiLanguage}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          chatHistory={chatHistory}
          historyLoading={historyLoading}
          sessionId={sessionId}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onClearHistory={handleClearHistory}
          onNewConversation={handleNewConversation}
          startingNewChat={startingNewChat}
          openAdminPortal={openAdminPortal}
          isSuperadmin={isSuperadminRole(authProfile?.role)}
          apiFetch={apiFetch}
          getApiHeaders={getApiHeaders}
          error={error}
          setError={setError}
          canSwitchProductMode={canSwitchProductMode(authProfile)}
          entitledModeOptions={entitledModeOptions}
          onConsultantModeChange={handleConsultantModeChange}
          savedReportHtml={report?.html_content}
          savedReportTitle={report?.title}
          savedUploadedFiles={uploadedFiles}
          onUploadedFilesChange={handleTier1UploadedFilesChange}
          savedTier1Draft={tier1Draft}
          onTier1DraftChange={handleTier1DraftChange}
          savedReportChatMessages={tier1ReportChatMessages}
          onReportChatMessagesChange={handleTier1ReportChatMessagesChange}
          onConversationRefresh={handleTier1ConversationRefresh}
          screenLanguage={tier1ScreenLanguage}
          reportLanguage={tier1ReportLanguage}
          onScreenLanguageChange={handleTier1ScreenLanguageChange}
          onReportLanguageChange={handleTier1ReportLanguageChange}
          onSaveLanguages={saveTier1Languages}
          onReportHtmlUpdated={handleTier1ReportHtmlUpdated}
          onRefreshKbStats={loadKbStats}
          kbStats={kbStats}
          kbSyncing={kbSyncing || kbReindexing}
          onKbSync={() => handleKbSync({ scrollToPanel: false })}
          onKbReindex={() => handleKbReindex({ scrollToPanel: false })}
          ingestUrl={ingestUrl}
          onIngestUrlChange={setIngestUrl}
          onIngestUrl={() => handleIngestUrl({ scrollToPanel: false })}
          ingestingUrl={ingestingUrl}
          onKbUpload={(files) => handleKbUpload(files, { scrollToPanel: false })}
          kbUploading={kbUploading}
          kbUploadFeedback={kbUploadFeedback}
          kbUploadVisible={kbUploadVisible}
          onKbUploadVisibleChange={setKbUploadVisible}
          onKbNoindexToggle={handleKbNoindexToggle}
          onKbDelete={handleKbDelete}
          kbNoindexUpdatingPath={kbNoindexUpdatingPath}
          kbDeletingPath={kbDeletingPath}
        />
      </>
    );
  }

  return (
    <div className="app-shell bg-black text-white text-sm">
      <div className="app-top-bar">
      {/* Header */}
      <header className="border-b border-teal-500/20">
        <div className="container mx-auto px-4 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center shrink-0">
              <Logo modeId={consultantMode} />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
              {canSwitchProductMode(authProfile) && (
              <div className="hidden md:block">
                <label htmlFor="consultant-mode" className="sr-only">Mode</label>
                <select
                  id="consultant-mode"
                  value={consultantMode}
                  onChange={(e) => handleConsultantModeChange(e.target.value)}
                  className="px-2 py-1.5 bg-gray-900 border border-teal-500/30 rounded-lg text-xs text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {entitledModeOptions.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      Mode: {mode.label}
                    </option>
                  ))}
                </select>
              </div>
              )}
              <div className="hidden md:block">
                <label htmlFor="consultant-specialty" className="sr-only">Specialty</label>
                <select
                  id="consultant-specialty"
                  value={consultantSpecialty}
                  onChange={(e) => handleConsultantSpecialtyChange(e.target.value)}
                  className="px-2 py-1.5 bg-gray-900 border border-teal-500/30 rounded-lg text-xs text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {(MODE_SPECIALTIES[consultantMode] || []).map((specialty) => (
                    <option key={specialty.id} value={specialty.id}>
                      Specialty: {specialty.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => setShowClientSupport(true)}
                className="px-2.5 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-teal-300 font-medium rounded-lg text-xs transition-all duration-200"
              >
                Help &amp; Requests
              </button>
              {isSuperadminRole(authProfile?.role) && (
                <button
                  type="button"
                  onClick={openAdminPortal}
                  className="px-2.5 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-teal-300 font-medium rounded-lg text-xs transition-all duration-200"
                >
                  Admin
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowReportBuilder((prev) => !prev)}
                className={`px-2.5 py-1.5 border font-medium rounded-lg text-xs transition-all duration-200 ${
                  showReportBuilder
                    ? 'bg-teal-500/20 border-teal-500/50 text-teal-300'
                    : 'bg-gray-900 hover:bg-gray-800 border-teal-500/30 text-white'
                }`}
              >
                {showReportBuilder ? 'Back to Chat' : 'Report Builder'}
              </button>
              <button
                type="button"
                onClick={toggleToolsSidebar}
                className="px-2.5 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-white font-medium rounded-lg text-xs transition-all duration-200"
              >
                {showToolsSidebar ? 'Hide Tools' : 'Show Tools'}
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={generatingReport || loading || messages.length < 2}
                className="px-2.5 py-1.5 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-medium rounded-lg text-xs transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingReport ? 'Generating…' : 'Generate Report'}
              </button>
              <button
                onClick={() => setShowHistory((prev) => !prev)}
                className="px-2.5 py-1.5 bg-gray-900 hover:bg-gray-800 border border-teal-500/30 text-white font-medium rounded-lg text-xs transition-all duration-200 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{showHistory ? 'Hide History' : 'History'}</span>
              </button>
              <button
                type="button"
                onClick={handleNewConversation}
                disabled={startingNewChat}
                className="px-2.5 py-1.5 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-medium rounded-lg text-xs transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>New Chat</span>
              </button>
              <div className="hidden sm:flex items-center gap-1.5">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" aria-hidden="true" />
                <span className="text-xs text-gray-400">Live</span>
              </div>
              {authProfile?.tenant_name && (
                <span className="hidden xl:inline text-xs text-teal-300/90 truncate max-w-[10rem]" title={authProfile.tenant_name}>
                  {authProfile.tenant_name}
                </span>
              )}
              {onLogout && getStoredAuthToken() && (
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="px-2 py-1.5 text-xs bg-gray-900 hover:bg-gray-800 border border-teal-500/30 rounded-lg text-gray-300"
                >
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="bg-gray-950 border-b border-teal-500/10">
        <div className="container mx-auto px-4 py-1.5 max-w-4xl">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-xs font-semibold text-teal-400 shrink-0">
              Mode: {CONSULTANT_MODES[consultantMode]?.label}
            </p>
            <p className="text-[11px] text-gray-500 truncate min-w-0 flex-1 hidden sm:block">
              {(MODE_SPECIALTIES[consultantMode] || []).find((item) => item.id === consultantSpecialty)?.description
                || CONSULTANT_MODES[consultantMode]?.description}
            </p>
            <div className="md:hidden w-full sm:w-auto sm:min-w-[12rem]">
              {canSwitchProductMode(authProfile) && (
              <>
              <label htmlFor="consultant-mode-mobile" className="sr-only">
                Mode
              </label>
              <select
                id="consultant-mode-mobile"
                value={consultantMode}
                onChange={(e) => handleConsultantModeChange(e.target.value)}
                className="w-full px-2 py-1 bg-gray-900 border border-teal-500/30 rounded-lg text-xs text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {entitledModeOptions.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    Mode: {mode.label}
                  </option>
                ))}
              </select>
              </>
              )}
            </div>
            <div className="md:hidden w-full sm:w-auto sm:min-w-[12rem]">
              <label htmlFor="consultant-specialty-mobile" className="sr-only">
                Specialty
              </label>
              <select
                id="consultant-specialty-mobile"
                value={consultantSpecialty}
                onChange={(e) => handleConsultantSpecialtyChange(e.target.value)}
                className="w-full px-2 py-1 bg-gray-900 border border-teal-500/30 rounded-lg text-xs text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {(MODE_SPECIALTIES[consultantMode] || []).map((specialty) => (
                  <option key={specialty.id} value={specialty.id}>
                    Specialty: {specialty.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div className="app-workspace">
        {showHistory && (
          <aside className="w-full md:w-80 lg:w-96 shrink-0 border-r border-teal-500/20 bg-gray-950 flex flex-col min-h-0 overflow-hidden max-h-[40vh] md:max-h-none">
            <div className="px-4 py-4 border-b border-teal-500/10 shrink-0 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-teal-400">Chat History</h2>
                <p className="text-xs text-gray-400 mt-1">Resume a previous consultation or delete individual entries with the trash icon.</p>
              </div>
              {chatHistory.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearHistory}
                  className="text-xs text-red-400/90 hover:text-red-300 shrink-0"
                  title={tUi(uiLanguage, 'appConfirmClearAllHistory')}
                >
                  {tUi(uiLanguage, 'appClearAllHistory')}
                </button>
              )}
            </div>
            <div className="app-scroll-pane p-3 space-y-2">
              {historyLoading ? (
                <div className="text-sm text-gray-400 px-2 py-4">Loading saved chats...</div>
              ) : chatHistory.length === 0 ? (
                <div className="text-sm text-gray-400 px-2 py-4">No saved chats yet. Start a conversation and it will appear here.</div>
              ) : (
                chatHistory.map((item) => (
                  <div
                    key={item.session_id}
                    onClick={() => handleSelectConversation(item.session_id)}
                    className={`w-full text-left rounded-lg p-3 border transition-all duration-200 cursor-pointer ${
                      item.session_id === sessionId
                        ? 'bg-teal-500/10 border-teal-500 text-white'
                        : 'bg-gray-900 border-teal-500/20 text-gray-200 hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{item.title || 'Untitled consultation'}</p>
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {`Mode: ${CONSULTANT_MODES[item.mode]?.label || item.mode}`}
                        </p>
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{item.preview}</p>
                        <p className="text-[11px] text-gray-500 mt-2">
                          {formatHistoryTimestamp(item.updated_at)} · {item.message_count} messages
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => handleDeleteConversation(item.session_id, event)}
                        className="text-gray-500 hover:text-red-400 p-1"
                        title={tUi(uiLanguage, 'appDeleteChatTitle')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        )}

        {showToolsSidebar && (
          <WorkspaceSidebar
            uiLanguage={uiLanguage}
            panels={workspacePanels}
            onTogglePanel={toggleWorkspacePanel}
            onShowAll={showAllWorkspacePanels}
            onHideAll={hideAllWorkspacePanels}
            isPlatformAdmin={isSuperadminRole(authProfile?.role)}
            nationStateConsentBlock={
              EXERCISE_CONSULTANT_MODES.has(consultantMode) ? (
                <div ref={consentRef} className="scroll-mt-4">
                  <NationStateConsent
                    uiLanguage={uiLanguage}
                    accepted={nationStateConsent}
                    acknowledgedBy={consentName}
                    onAcknowledgedByChange={setConsentName}
                    onAccept={async () => {
                      try {
                        await handleNationStateConsent();
                      } catch (err) {
                        setError(err.message || tUi(uiLanguage, 'appErrorRecordConsent'));
                      }
                    }}
                  />
                </div>
              ) : null
            }
            childrenByPanel={{
              engagement: (
                <div ref={engagementRef}>
                  <EngagementBar
                    uiLanguage={uiLanguage}
                    clientName={clientName}
                    projectName={projectName}
                    reportTemplateId={reportTemplateId}
                    reportTemplates={reportTemplates}
                    classification={classification}
                    customClassification={customClassification}
                    chatLanguage={chatLanguage}
                    reportLanguage={reportLanguage}
                    analystLanguage={analystLanguage}
                    customChatLanguage={customChatLanguage}
                    customReportLanguage={customReportLanguage}
                    customAnalystLanguage={customAnalystLanguage}
                    locale={locale}
                    languageRegister={languageRegister}
                    exportLanguageMode={exportLanguageMode}
                    onClientNameChange={setClientName}
                    onProjectNameChange={setProjectName}
                    onReportTemplateChange={setReportTemplateId}
                    onClassificationChange={setClassification}
                    onCustomClassificationChange={setCustomClassification}
                    onChatLanguageChange={setChatLanguage}
                    onReportLanguageChange={setReportLanguage}
                    onAnalystLanguageChange={setAnalystLanguage}
                    onCustomChatLanguageChange={setCustomChatLanguage}
                    onCustomReportLanguageChange={setCustomReportLanguage}
                    onCustomAnalystLanguageChange={setCustomAnalystLanguage}
                    onLocaleChange={setLocale}
                    onLanguageRegisterChange={setLanguageRegister}
                    onExportLanguageModeChange={handleExportLanguageModeChange}
                    onTranslateChat={handleTranslateChat}
                    translatingChat={translatingChat}
                    onSave={saveEngagement}
                    saving={savingEngagement}
                  />
                  <div className="mt-4 rounded-lg border border-teal-500/30 bg-gray-900 p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-teal-300 mb-1">
                        {tUi(uiLanguage, 'reportInstructionsLabel')}
                      </label>
                      <p className="text-[10px] text-gray-500 mb-2">
                        {tUi(uiLanguage, 'reportInstructionsHint')}
                      </p>
                      {reportPromptId && (
                        <div className="mb-2 rounded border border-emerald-500/30 bg-emerald-950/20 px-3 py-2 space-y-2">
                          <p className="text-xs text-emerald-200">
                            {tUi(uiLanguage, 'promptLibraryAppliedToSession')}:{' '}
                            <span className="font-medium">{reportPromptName || reportPromptId}</span>
                          </p>
                          {!canViewPromptLibraryContent(authProfile) && (
                            <p className="text-[10px] text-gray-500">{tUi(uiLanguage, 'promptLibrarySelectToApply')}</p>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setReportPromptId(null);
                              setReportPromptName(null);
                              setReportInstructions('');
                              reportInstructionsApplyRef.current = {
                                sessionId: sessionIdRef.current,
                                promptId: null,
                                promptName: null,
                                text: '',
                              };
                              saveCurrentConversation({
                                reportInstructions: '',
                                reportPromptId: null,
                                reportPromptName: null,
                              }).catch(() => {});
                            }}
                            className="px-3 py-1.5 rounded border border-red-500/40 text-red-300 text-xs hover:bg-red-950/40"
                          >
                            {tUi(uiLanguage, 'commonRemove')}
                          </button>
                        </div>
                      )}
                      <textarea
                        value={reportInstructions}
                        readOnly={Boolean(reportPromptId && !canWritePromptLibrary(authProfile))}
                        onChange={(e) => {
                          setReportInstructions(e.target.value);
                          reportInstructionsApplyRef.current = {
                            sessionId: sessionIdRef.current,
                            promptId: reportPromptId,
                            promptName: reportPromptName,
                            text: e.target.value,
                          };
                          if (reportPromptId) {
                            setReportPromptId(null);
                            setReportPromptName(null);
                          }
                        }}
                        onBlur={() => {
                          if (savingReportPromptLibrary || applyingReportPromptRef.current) return;
                          const snap = reportInstructionsApplyRef.current;
                          const useSnapshot = snap.sessionId === sessionIdRef.current && snap.text;
                          const instructionsToSave = useSnapshot ? snap.text : reportInstructions;
                          const promptIdToSave = useSnapshot ? snap.promptId : reportPromptId;
                          const promptNameToSave = useSnapshot ? snap.promptName : reportPromptName;
                          if (promptIdToSave && !instructionsToSave.trim()) return;
                          saveCurrentConversation({
                            reportInstructions: instructionsToSave,
                            reportPromptId: promptIdToSave,
                            reportPromptName: promptNameToSave,
                          }).catch(() => {});
                        }}
                        placeholder={tUi(uiLanguage, 'reportInstructionsPlaceholder')}
                        className="w-full h-24 bg-gray-950 text-white text-sm p-2 rounded border border-teal-500/30 focus:border-teal-400 outline-none read-only:opacity-90"
                      />
                    </div>
                  </div>
                </div>
              ),
              reportPromptLibrary: (
                <div ref={reportPromptLibraryRef}>
                  <PromptLibraryPanel
                    uiLanguage={uiLanguage}
                    category="report"
                    prompts={reportPromptLibraryEntries}
                    loading={reportPromptLibraryLoading}
                    saving={savingReportPromptLibrary}
                    error={reportPromptLibraryError}
                    currentPromptText={reportInstructions}
                    appliedMandateId={reportPromptId}
                    appliedMandateName={reportPromptName}
                    appliedSessionText={reportPromptId ? appliedMandateText : ''}
                    readOnly={!canWritePromptLibrary(authProfile)}
                    canEdit={canWritePromptLibrary(authProfile)}
                    onRefresh={loadReportPromptLibrary}
                    onSaveNew={saveReportPromptLibraryEntry}
                    onUpdate={updateReportPromptLibraryEntry}
                    onApply={applyReportPromptLibraryEntry}
                    onBeforeApply={beginApplyingReportPrompt}
                    onSendSession={sendAppliedSessionPrompt}
                    onDelete={deleteReportPromptLibraryEntry}
                  />
                </div>
              ),
              glossary: (
                <div ref={glossaryRef}>
                  <GlossaryPanel
                    uiLanguage={uiLanguage}
                    terms={glossaryTerms}
                    userLanguageLabel={getLanguageLabel(activeUserLanguage(chatLanguage, customChatLanguage))}
                    clientLanguageLabel={getLanguageLabel(activeClientLanguage(reportLanguage, customReportLanguage))}
                    loading={glossaryLoading}
                    saving={savingGlossary}
                    translating={translatingGlossary}
                    readOnly={!canWriteConfig(authProfile)}
                    onSave={saveGlossary}
                    onRefresh={loadGlossary}
                    onTranslateToClient={translateGlossaryToClient}
                  />
                </div>
              ),
              pretext: (
                <div ref={pretextRef}>
                  <PretextStudio
                    uiLanguage={uiLanguage}
                    pretexts={pretexts}
                    loading={pretextsLoading}
                    onRefresh={loadPretexts}
                    onCreate={handleCreatePretext}
                    onUpdate={handleUpdatePretext}
                    onDelete={handleDeletePretext}
                    onExportPack={handleExportPretextPack}
                    onExtractFromChat={handleExtractPretexts}
                    extracting={extractingPretexts}
                    extractFeedback={pretextExtractFeedback}
                  />
                </div>
              ),
              promptLibrary: (
                <div ref={promptLibraryRef}>
                  <PromptLibraryPanel
                    uiLanguage={uiLanguage}
                    prompts={promptLibraryEntries}
                    loading={promptLibraryLoading}
                    saving={savingPromptLibrary}
                    error={promptLibraryError}
                    currentPromptText={operationalMandates}
                    appliedMandateId={mandatePromptId}
                    appliedMandateName={mandatePromptName}
                    appliedSessionText={appliedMandateText}
                    readOnly={!canWritePromptLibrary(authProfile)}
                    canEdit={canWritePromptLibrary(authProfile)}
                    onRefresh={loadPromptLibrary}
                    onSaveNew={savePromptLibraryEntry}
                    onUpdate={updatePromptLibraryEntry}
                    onApply={applyPromptLibraryEntry}
                    onSendSession={sendAppliedSessionPrompt}
                    onDelete={deletePromptLibraryEntry}
                  />
                </div>
              ),
              knowledge: (
                <div ref={knowledgeRef}>
                  <KnowledgePanel
                    uiLanguage={uiLanguage}
                    stats={kbStats}
                    syncing={kbSyncing || kbReindexing}
                    onSync={handleKbSync}
                    onReindex={handleKbReindex}
                    ingestUrl={ingestUrl}
                    onIngestUrlChange={setIngestUrl}
                    onIngestUrl={handleIngestUrl}
                    ingesting={ingestingUrl}
                    isPlatformAdmin={isSuperadminRole(authProfile?.role)}
                    isOrgAdmin={isOrgAdminRole(authProfile?.role)}
                    canSyncKb={canSyncKb(authProfile)}
                    canFeedLlm={canFeedLlm(authProfile)}
                    canManageNoindex={canManageKbNoindex(authProfile)}
                    feedFileAccept={getFeedFileAccept(authProfile)}
                    onUploadFiles={handleKbUpload}
                    uploading={kbUploading}
                    uploadFeedback={kbUploadFeedback}
                    uploadVisible={kbUploadVisible}
                    onUploadVisibleChange={setKbUploadVisible}
                    kbFiles={kbStats?.files || []}
                    onToggleNoindex={handleKbNoindexToggle}
                    onDeleteFile={handleKbDelete}
                    noindexUpdatingPath={kbNoindexUpdatingPath}
                    deletingPath={kbDeletingPath}
                  />
                </div>
              ),
            }}
          />
        )}

      {/* Main Content — Report Builder view or chat/report */}
      {showReportBuilder ? (
        <main className="app-workspace-main">
          <div className="app-scroll-pane">
          <ReportBuilderPanel
            initialClientName={clientName}
            initialClassification={
              classification && classification !== '__custom__'
                ? classification
                : customClassification || 'CONFIDENTIAL'
            }
            onSendToChat={(prompt) => {
              setShowReportBuilder(false);
              setInputMessage(prompt);
            }}
          />
          </div>
        </main>
      ) : (
      <main className="app-workspace-main px-6 py-4">
        {hostRebootAlert && (
          <div className="shrink-0 -mx-6 mb-3">
            <HostAlertBanner
              title={hostRebootAlert.title}
              detail={hostRebootAlert.detail}
            />
          </div>
        )}
        {!showToolsSidebar && (
          <div className="shrink-0 mb-3 rounded-lg border border-teal-500/20 bg-gray-900/60 px-4 py-2 text-sm text-gray-400">
            {tUi(uiLanguage, 'appToolsPanelHiddenPrefix')}{' '}
            <strong className="text-teal-300">{tUi(uiLanguage, 'appShowTools')}</strong>{' '}
            {tUi(uiLanguage, 'appToolsPanelHiddenSuffix')}
          </div>
        )}
        {actionFeedback && (
          <div
            className={`shrink-0 mb-3 rounded-lg border px-4 py-2 text-sm flex items-center justify-between gap-3 ${
              actionFeedback.variant === 'error'
                ? 'border-red-500/40 bg-red-950/40 text-red-100'
                : actionFeedback.variant === 'warning'
                  ? 'border-amber-500/40 bg-amber-950/40 text-amber-100'
                  : 'border-teal-500/40 bg-teal-950/40 text-teal-100'
            }`}
            role="status"
            aria-live="polite"
          >
            <span>{actionFeedback.message}</span>
            <button
              type="button"
              onClick={() => setActionFeedback(null)}
              className={`text-xs shrink-0 ${
                actionFeedback.variant === 'error'
                  ? 'text-red-300 hover:text-red-200'
                  : actionFeedback.variant === 'warning'
                    ? 'text-amber-300 hover:text-amber-200'
                    : 'text-teal-300 hover:text-teal-200'
              }`}
            >
              {tUi(uiLanguage, 'appDismiss')}
            </button>
          </div>
        )}
        <div ref={mainScrollRef} className="app-scroll-pane space-y-4 mb-4">

        {/* Chat Messages */}
        <div ref={chatSectionRef} className="scroll-mt-4 space-y-4">
          {policyRefusalHint && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-950/25 p-4 text-sm text-amber-100">
              <p className="font-semibold text-amber-300 mb-2">The model declined this request</p>
              <p className="mb-2">
                Some models block Nation State mode phrasing even for authorized red-team work. Try these steps:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-amber-100/90">
                <li>In the Tools panel, save Engagement details before retrying.</li>
                <li>Rephrase your ask as an <strong>authorized exercise deliverable</strong> (threat emulation scenario, pretext draft for approved test, control gap analysis).</li>
                <li>Avoid “hack / attack this person”; use “assumed adversary exercise for client defensive assessment”.</li>
                <li>Restart the API after updating prompts, then send one focused follow-up message.</li>
                <li>For general security advice without emulation framing, switch to <strong>Social Engineering Consultant</strong> mode.</li>
              </ul>
            </div>
          )}
          {messagesBaseline && (
            <div className="rounded-lg border border-cyan-500/25 bg-cyan-950/20 p-4">
              <p className="text-sm text-cyan-200 mb-3">
                Chat translation review — compare original messages with the client-language version.
              </p>
              <div className="flex flex-wrap gap-2">
                {REPORT_REVIEW_MODES.map((mode) => (
                  <button
                    key={`chat-${mode.code}`}
                    type="button"
                    onClick={() => {
                      setChatReviewMode(mode.code);
                      saveCurrentConversation({ chatReviewMode: mode.code }).catch((err) => {
                        console.error('Failed to save chat review mode:', err);
                      });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      chatReviewMode === mode.code
                        ? 'bg-cyan-500 text-black'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-teal-500/20'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(() => {
            let lastAssistantMessageIndex = -1;
            for (let i = messages.length - 1; i >= 0; i -= 1) {
              if (messages[i].role === 'assistant') {
                lastAssistantMessageIndex = i;
                break;
              }
            }
            return messages.map((message, index) => {
            const baselineMessage = messagesBaseline?.[index];
            const isLatestAssistant = index === lastAssistantMessageIndex;
            const assistantScrollRef = isLatestAssistant ? latestAssistantMessageRef : undefined;
            const showSideBySide = messagesBaseline
              && chatReviewMode === 'side_by_side'
              && baselineMessage
              && (message.role === 'user' || message.role === 'assistant');

            if (showSideBySide) {
              return (
                <div key={index} className="grid gap-3 lg:grid-cols-2">
                  <div className={`rounded-lg p-4 border ${
                    baselineMessage.role === 'user'
                      ? 'bg-teal-500/20 border-teal-500/40 text-teal-100'
                      : 'bg-gray-900 text-white border-teal-500/30'
                  }`}>
                    <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Original</p>
                    <div className="whitespace-pre-wrap">{formatChatMessageForDisplay(baselineMessage)}</div>
                  </div>
                  <div
                    ref={message.role === 'assistant' ? assistantScrollRef : undefined}
                    className={`rounded-lg p-4 border ${
                    message.role === 'user'
                      ? 'bg-teal-500 text-black'
                      : 'bg-gray-900 text-white border-cyan-500/30'
                  }`}>
                    <p className={`text-[11px] uppercase tracking-wide mb-2 ${
                      message.role === 'user' ? 'text-black/60' : 'text-cyan-400'
                    }`}>
                      Translation
                    </p>
                    <div
                      className="whitespace-pre-wrap select-text"
                      onMouseUp={(e) => message.role === 'assistant' && handleAssistantTextSelect(index, e.currentTarget)}
                    >
                      {formatChatMessageForDisplay(message)}
                    </div>
                    {message.role === 'assistant' && (
                      <CitationsPanel
                        citations={message.citations}
                        groundingCount={message.groundingCount}
                        filesInContext={message.filesInContext ?? 0}
                        showUnverifiedWarning={message.role === 'assistant' && !message.isStreaming}
                        uiLanguage={uiLanguage}
                      />
                    )}
                    {message.role === 'assistant' && !isUploadAckMessage(message.content) && (
                      <AssistantFollowUp
                        messageIndex={index}
                        answerContent={message.content}
                        selectedExcerpt={getFollowUpExcerpt(index)}
                        onClearSelection={() => setFollowUpSelection(null)}
                        onSubmit={handleFollowUpSubmit}
                        disabled={loading || isProcessingRef.current}
                        uiLanguage={uiLanguage}
                      />
                    )}
                  </div>
                </div>
              );
            }

            if (messagesBaseline && chatReviewMode === 'original' && baselineMessage) {
              const displayMessage = baselineMessage;
              return (
                <div
                  key={index}
                  className={`flex ${displayMessage.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    ref={displayMessage.role === 'assistant' ? assistantScrollRef : undefined}
                    className={`max-w-[80%] rounded-lg p-4 ${
                      displayMessage.role === 'user'
                        ? 'bg-teal-500 text-black'
                        : 'bg-gray-900 text-white border border-teal-500/30'
                    }`}
                  >
                    <div
                      className="whitespace-pre-wrap select-text"
                      onMouseUp={(e) => displayMessage.role === 'assistant' && handleAssistantTextSelect(index, e.currentTarget)}
                    >
                      {formatChatMessageForDisplay(displayMessage)}
                    </div>
                    {displayMessage.role === 'assistant' && (
                      <CitationsPanel
                        citations={displayMessage.citations}
                        groundingCount={displayMessage.groundingCount}
                        filesInContext={displayMessage.filesInContext ?? 0}
                        showUnverifiedWarning={!displayMessage.isStreaming}
                        uiLanguage={uiLanguage}
                      />
                    )}
                    {displayMessage.role === 'assistant' && !isUploadAckMessage(displayMessage.content) && (
                      <AssistantFollowUp
                        messageIndex={index}
                        answerContent={displayMessage.content}
                        selectedExcerpt={getFollowUpExcerpt(index)}
                        onClearSelection={() => setFollowUpSelection(null)}
                        onSubmit={handleFollowUpSubmit}
                        disabled={loading || isProcessingRef.current}
                        uiLanguage={uiLanguage}
                      />
                    )}
                    {displayMessage.timestamp && (
                      <div className="text-xs mt-2 opacity-70">
                        {new Date(displayMessage.timestamp).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            return (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                ref={message.role === 'assistant' ? assistantScrollRef : undefined}
                className={`max-w-[80%] rounded-lg p-4 ${
                  message.role === 'user'
                    ? 'bg-teal-500 text-black'
                    : 'bg-gray-900 text-white border border-teal-500/30'
                }`}
              >
                <div
                  className="whitespace-pre-wrap select-text"
                  onMouseUp={(e) => message.role === 'assistant' && handleAssistantTextSelect(index, e.currentTarget)}
                >
                  {formatChatMessageForDisplay(message)}
                </div>
                {message.role === 'assistant' && (
                  <CitationsPanel
                    citations={message.citations}
                    groundingCount={message.groundingCount}
                    filesInContext={message.filesInContext ?? 0}
                    showUnverifiedWarning={!message.isStreaming}
                    uiLanguage={uiLanguage}
                  />
                )}
                {message.role === 'assistant' && !isUploadAckMessage(message.content) && (
                  <AssistantFollowUp
                    messageIndex={index}
                    answerContent={message.content}
                    selectedExcerpt={getFollowUpExcerpt(index)}
                    onClearSelection={() => setFollowUpSelection(null)}
                    onSubmit={handleFollowUpSubmit}
                    disabled={loading || isProcessingRef.current}
                    uiLanguage={uiLanguage}
                  />
                )}
                {message.timestamp && (
                  <div className="text-xs mt-2 opacity-70">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
            );
          });
          })()}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-900 rounded-lg p-4 border border-teal-500/30">
                <div className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5 text-teal-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-300">Thinking...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div ref={errorRef} className="scroll-mt-4 mb-4 bg-red-900/30 border border-red-500/50 rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-300 font-medium">Error</span>
              </div>
              {reportGenFailed && (
                <button
                  type="button"
                  onClick={handleGenerateReport}
                  disabled={generatingReport || loading || messages.length < 2}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black disabled:text-gray-400 transition-colors"
                >
                  Retry Report
                </button>
              )}
            </div>
            <p className="text-red-200 mt-2">{error}</p>
          </div>
        )}

        {/* Report Display */}
        <div ref={reportSectionRef} className="scroll-mt-4">
        {showReport && reportGenWarnings.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-950/20 p-4">
            <p className="text-amber-300 font-medium text-sm">Report generated with warnings</p>
            <ul className="mt-2 space-y-1 text-sm text-amber-200/90">
              {reportGenWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
        {showReport && report && (
          <ReportReviewPanel
            uiLanguage={uiLanguage}
            report={report}
            baseline={reportBaseline}
            reviewMode={reportReviewMode}
            onReviewModeChange={(mode) => {
              setReportReviewMode(mode);
              saveCurrentConversation({ reportReviewMode: mode }).catch((err) => {
                console.error('Failed to save report review mode:', err);
              });
            }}
            sourceLabel={getLanguageLabel(reportSourceLanguage || resolveReportSourceLanguage(
              reportLanguage,
              customReportLanguage,
              analystLanguage,
              customAnalystLanguage,
            ))}
            targetLabel={getLanguageLabel(reportTargetLanguage || pickTranslationTarget(
              reportLanguage,
              customReportLanguage,
              chatLanguage,
              customChatLanguage,
            ) || 'Translation')}
            reportQa={reportQa}
            translatingReport={translatingReport}
            runningReportQa={runningReportQa}
            exportLanguageMode={exportLanguageMode}
            translateButtonLabel={`Translate Report (${getLanguageLabel(pickTranslationTarget(reportLanguage, customReportLanguage, chatLanguage, customChatLanguage) || 'set language')})`}
            onTranslate={handleTranslateReport}
            onReportQa={handleReportQa}
            onDownloadDocx={() => handleDownloadReport('docx')}
            onDownloadPdf={() => handleDownloadReport('pdf')}
            onDownloadPptx={() => handleDownloadReport('pptx')}
            downloadingReportFormat={downloadingReport}
            pretexts={pretexts}
            onRemoveSectionFromReport={undefined}
            onExcludePretextFromReport={undefined}
            reportExcludedSections={reportExcludedSections}
            onReportExcludedSectionsChange={handleReportExcludedSectionsChange}
            reportExcludedSectionKeys={reportExcludedSectionKeys}
            onReportExcludedSectionKeysChange={handleReportExcludedSectionKeysChange}
          />
        )}

        {generatingReport && !showReport && (
          <div className="mb-4 rounded-lg border border-teal-500/30 bg-gray-900/80 p-6">
            <div className="flex items-center justify-center gap-3">
              <p className="text-teal-200 font-medium text-center">{reportProgressStep || 'Report generation in progress'}</p>
              {reportStartedAt != null && <ElapsedTimer startedAt={reportStartedAt} />}
            </div>
            {reportProgressDetail && (
              <p className="text-sm text-teal-400/80 text-center mt-1">{reportProgressDetail}</p>
            )}
            {typeof reportProgressPercent === 'number' && (
              <div className="mt-4 flex items-center gap-3 max-w-lg mx-auto">
                <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-teal-500 transition-all duration-500 ease-out"
                    style={{ width: `${Math.min(100, Math.max(0, reportProgressPercent))}%` }}
                  />
                </div>
                <span className="shrink-0 text-xs tabular-nums text-gray-400">
                  {Math.round(reportProgressPercent)}%
                </span>
              </div>
            )}
            {reportProgressSteps.length > 0 && (
              <ul className="mt-4 space-y-1.5 max-w-lg mx-auto text-left">
                {reportProgressSteps.map((step) => (
                  <li
                    key={step.key}
                    className={`text-sm ${
                      step.state === 'active'
                        ? 'text-teal-200'
                        : step.state === 'done'
                          ? 'text-gray-500'
                          : 'text-gray-600'
                    }`}
                  >
                    <span className="mr-2" aria-hidden="true">
                      {step.state === 'done' ? '✓' : step.state === 'active' ? '●' : '○'}
                    </span>
                    <span className={step.state === 'done' ? 'line-through' : ''}>{step.label}</span>
                    {step.state === 'active' && (
                      <span className="block pl-5 text-xs text-gray-400 mt-0.5">{step.message}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-sm text-gray-400 mt-4 text-center">
              Live progress also appears above the chat input. Use Stop to cancel, then Retry if needed.
            </p>
            {reportGenWarnings.length > 0 && (
              <ul className="mt-3 text-left text-xs text-amber-300/90 space-y-1 max-w-lg mx-auto">
                {reportGenWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        </div>

        {/* Uploaded Files Display */}
        {uploadedFiles.length > 0 && (
          <div className="mb-4 bg-gray-900 rounded-lg p-4 border border-teal-500/30">
            <h3 className="text-sm font-semibold text-teal-400 mb-2">Uploaded Files:</h3>
            <div className="space-y-2">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-800 rounded p-2">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-white text-sm">{file.filename}</span>
                    <span className="text-gray-400 text-xs">({file.file_type})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WRAITH Operations - visible only in nation_state / WRAITH mode */}
        {consultantMode === 'nation_state' && (
          <details className="mb-4 bg-gray-900 rounded-lg border border-red-500/30" open>
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-red-400 flex items-center justify-between">
              WRAITH Operations
              <span className="text-xs text-gray-400 font-normal">(Mandates + Tradecraft injected into every prompt)</span>
            </summary>
            <div className="px-4 pb-4 space-y-4 border-t border-red-500/20 pt-4">
              <div>
                <label className="block text-xs text-red-300 mb-1">Operational Mandates (free-text, operator-defined)</label>
                {mandatePromptId && (
                  <div className="mb-2 rounded border border-emerald-500/30 bg-emerald-950/20 px-3 py-2 space-y-2">
                    <p className="text-xs text-emerald-200">
                      {tUi(uiLanguage, 'promptLibraryAppliedMandateLabel')}: <span className="font-medium">{mandatePromptName || mandatePromptId}</span>
                    </p>
                    {!canWritePromptLibrary(authProfile) && (
                      <p className="text-[10px] text-gray-500">{tUi(uiLanguage, 'promptLibrarySelectToApply')}</p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setMandatePromptId(null);
                        setMandatePromptName(null);
                        setOperationalMandates('');
                        setAppliedMandateText('');
                        mandateApplyRef.current = { sessionId: null, promptId: null, promptName: null, text: '' };
                        setInputMessage('');
                        saveCurrentConversation({
                          operationalMandates: '',
                          mandatePromptId: null,
                          mandatePromptName: null,
                        }).catch(() => {});
                      }}
                      className="px-3 py-1.5 rounded border border-red-500/40 text-red-300 text-xs hover:bg-red-950/40"
                    >
                      {tUi(uiLanguage, 'commonRemove')}
                    </button>
                  </div>
                )}
                {(canViewPromptLibraryContent(authProfile) || !mandatePromptId) && (
                <textarea
                  value={operationalMandates}
                  readOnly={Boolean(mandatePromptId && !canWritePromptLibrary(authProfile))}
                  onChange={(e) => {
                    setOperationalMandates(e.target.value);
                    if (mandatePromptId) {
                      setMandatePromptId(null);
                      setMandatePromptName(null);
                    }
                  }}
                  onBlur={() => {
                    if (savingPromptLibrary || applyingMandatePromptRef.current) return;
                    saveCurrentConversation({
                      operationalMandates,
                      mandatePromptId: mandatePromptId,
                      mandatePromptName: mandatePromptName,
                    }).catch(() => {});
                  }}
                  placeholder="e.g. Prioritize elicitation of X; Avoid Y; Focus on Z vector..."
                  className="w-full h-20 bg-gray-950 text-white text-sm p-2 rounded border border-red-500/30 focus:border-red-400 outline-none read-only:opacity-90"
                />
                )}
                <p className="text-[10px] text-gray-500 mt-1">These will be placed at the VERY TOP of the system prompt in WRAITH mode.</p>
              {/* WRAITH Elicitation Content Pipeline (Phase 4) — gated to nation_state */}
              <div className="mt-4 pt-4 border-t border-red-500/20">
                <div className="text-xs text-red-300 mb-2 font-semibold">Elicitation Content Pipeline (Localization + Review)</div>
                <div className="text-[10px] text-gray-400">Intent Schema persisted • Locale Packs seeded • QA Critic (8-dim) • Back-Translation gate • Native Review Portal • Versioned Artifact Repo • Hard release gates • Deployment API ready. All flows respect WRAITH entitlement and operator-mandate supremacy.</div>
              </div>
              </div>

              <div>
                <label className="block text-xs text-red-300 mb-1">Authoritative Tradecraft Sources (select uploaded docs)</label>
                {uploadedFiles.length > 0 ? (
                  <div className="space-y-1 max-h-32 overflow-auto bg-gray-950 p-2 rounded border border-red-500/20">
                    {uploadedFiles.map((file, idx) => {
                      const fid = file.filename;
                      const checked = tradecraftSourceIds.includes(fid);
                      return (
                        <label key={idx} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...tradecraftSourceIds, fid]
                                : tradecraftSourceIds.filter((x) => x !== fid);
                              setTradecraftSourceIds(next);
                              saveCurrentConversation({ tradecraftSourceIds: next }).catch(() => {});
                            }}
                            className="accent-red-500"
                          />
                          <span>{fid}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Upload tradecraft documents in Knowledge Panel to select authoritative sources.</p>
                )}
                <p className="text-[10px] text-gray-500 mt-1">Selected docs will be treated as high-priority spy craft / tradecraft references (replacing generic LLM knowledge).</p>
              </div>

              <div>
                <label className="block text-xs text-red-300 mb-1">WRAITH Generation Mode</label>
                <div className="flex gap-4 text-sm">
                  {[
                    { v: 'offensive', l: 'Offensive elicitation' },
                    { v: 'defensive', l: 'Defensive / counter-elicitation' },
                    { v: 'both', l: 'Both (paired)' },
                  ].map((opt) => (
                    <label key={opt.v} className="flex items-center gap-1 text-white cursor-pointer">
                      <input
                        type="radio"
                        name="wraithGenMode"
                        value={opt.v}
                        checked={wraithGenerationMode === opt.v}
                        onChange={() => {
                          setWraithGenerationMode(opt.v);
                          saveCurrentConversation({ wraithGenerationMode: opt.v }).catch(() => {});
                        }}
                        className="accent-red-500"
                      />
                      {opt.l}
                    </label>
                  ))}
                </div>
              </div>

              <div className="text-[10px] text-red-400/80 bg-red-950/30 p-2 rounded">
                Explicit permission granted: Use real manipulation, psychological levers, and spycraft tactics drawn from the selected tradecraft documents + operator mandates. Goal: replace generic model knowledge with operator-supplied directives.
              </div>
            </div>
          </details>
        )}

        {/* Service Inquiry Form */}
        {CONSULTANT_MODES[consultantMode]?.supportsServices && askAboutServices && showServiceForm && (
          <div className="mb-4 bg-gray-900 rounded-lg p-6 border border-teal-500/30">
            <h3 className="text-xl font-semibold text-teal-400 mb-4">Get in Touch with Our Team</h3>
            <ServiceInquiryForm 
              onSubmit={handleServiceInquiry}
              onCancel={() => setShowServiceForm(false)}
              loading={loading}
            />
          </div>
        )}

        {/* Service Inquiry Prompt */}
        {CONSULTANT_MODES[consultantMode]?.supportsServices && askAboutServices && !showServiceForm && (
          <div className="mb-4 bg-gray-900 rounded-lg p-4 border border-teal-500/30">
            <p className="text-white mb-4">Would you like help implementing these recommendations?</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowServiceForm(true)}
                className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold rounded-lg transition-all duration-200"
              >
                Yes, have someone reach out
              </button>
              <button
                onClick={() => {
                  setAskAboutServices(false);
                  const declineMessage = {
                    role: 'assistant',
                    content: "Understood! If you need any assistance in the future, feel free to reach out. Good luck with implementing the recommendations!",
                    timestamp: new Date().toISOString()
                  };
                  setMessages(prev => [...prev, declineMessage]);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200"
              >
                No, I'll handle it myself
              </button>
            </div>
          </div>
        )}

        </div>

        <div className="shrink-0">
        {hostRebootAlert && (
          <div className="-mx-6 mb-3">
            <HostAlertBanner
              title={hostRebootAlert.title}
              detail={hostRebootAlert.detail}
            />
          </div>
        )}
        {activeActivity && (
          <div className="-mx-6 mb-3">
            <ActivityBanner
              label={activeActivity.label}
              detail={activeActivity.detail}
              progress={activeActivity.progress}
              steps={activeActivity.steps}
              onCancel={activeActivity.onCancel}
              warnings={activeActivity.warnings}
              startedAt={activeActivity.startedAt}
            />
          </div>
        )}
        {/* Conversation Mode - Glowing Orb */}
        {conversationMode ? (
          <div className="bg-gray-900 rounded-lg p-8 border border-teal-500/30 flex flex-col items-center justify-center">
            <button
              type="button"
              onClick={() => {
                if (!isSpeaking && !isProcessingRef.current && recognitionRef.current) {
                  try {
                    console.log('🔵 Orb clicked - starting recognition');
                    console.log('Current state:', {
                      isListening,
                      isSpeaking,
                      isProcessing: isProcessingRef.current,
                      conversationMode: conversationModeRef.current,
                      continuous: recognitionRef.current?.continuous
                    });
                    recognitionRef.current.start();
                    console.log('✅ Recognition started from orb click');
                  } catch (err) {
                    console.log('❌ Error starting recognition from orb:', err.message);
                    // If it fails, try again after a short delay
                    setTimeout(() => {
                      try {
                        if (conversationModeRef.current && recognitionRef.current) {
                          recognitionRef.current.start();
                          console.log('✅ Recognition started on retry');
                        }
                      } catch (retryErr) {
                        console.log('❌ Retry failed:', retryErr.message);
                      }
                    }, 500);
                  }
                } else {
                  console.log('⚠️ Orb click ignored - conditions not met:', {
                    isSpeaking,
                    isProcessing: isProcessingRef.current,
                    hasRecognition: !!recognitionRef.current
                  });
                }
              }}
              className="relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-full"
              disabled={isListening || isSpeaking || isProcessingRef.current}
            >
              {/* Glowing Orb */}
              <div className={`relative w-32 h-32 rounded-full transition-all duration-300 ${
                isListening 
                  ? 'bg-teal-500 animate-pulse' 
                  : isSpeaking
                  ? 'bg-teal-400'
                  : 'bg-teal-500/50 hover:bg-teal-500/70'
              }`}
              style={{
                boxShadow: isListening 
                  ? '0 0 40px rgba(20, 184, 166, 0.8), 0 0 80px rgba(20, 184, 166, 0.6), 0 0 120px rgba(20, 184, 166, 0.4)' 
                  : isSpeaking
                  ? '0 0 30px rgba(20, 184, 166, 0.6), 0 0 60px rgba(20, 184, 166, 0.4)'
                  : '0 0 20px rgba(20, 184, 166, 0.4)'
              }}>
                {/* Inner glow */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 opacity-60"></div>
                
                {/* Microphone icon in center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {isSpeaking ? (
                    <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                  ) : (
                    <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  )}
                </div>
              </div>
              
              {/* Ripple effect when listening */}
              {isListening && (
                <>
                  <div className="absolute inset-0 rounded-full bg-teal-500/30 animate-ping" style={{ animationDuration: '2s' }}></div>
                  <div className="absolute inset-0 rounded-full bg-teal-500/20 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                </>
              )}
            </button>
            
            {/* Status text */}
            <div className="mt-6 text-center">
              <p className="text-teal-400 font-semibold text-lg">
                {isListening ? 'Listening...' : isSpeaking ? 'Speaking...' : 'Ready'}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {isListening 
                  ? 'Speak naturally - I\'ll respond automatically' 
                  : isSpeaking
                  ? 'Please wait while I respond'
                  : 'Click the orb to start listening'}
              </p>
            </div>
            
            {/* Stop button */}
            <button
              type="button"
              onClick={stopListening}
              className="mt-6 px-6 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              <span>Exit Conversation Mode</span>
            </button>
          </div>
        ) : (
          /* Normal Input Form */
          canUseChat(authProfile) ? (
          <>
          {appliedMandateText.trim() && !conversationMode && (
            <div className="mb-3 rounded-lg border border-emerald-500/40 bg-emerald-950/30 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-300/90">
                    {tUi(uiLanguage, 'promptLibrarySessionPromptLabel')}
                  </p>
                  {mandatePromptName || reportPromptName ? (
                    <p className="text-sm text-emerald-100 font-medium mt-1">{mandatePromptName || reportPromptName}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => sendAppliedSessionPrompt(appliedMandateText)}
                  disabled={loading || !appliedMandateText.trim()}
                  className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold"
                >
                  {tUi(uiLanguage, 'promptLibrarySendSessionPrompt')}
                </button>
              </div>
              <pre className="text-sm text-white whitespace-pre-wrap max-h-40 overflow-y-auto">{appliedMandateText}</pre>
              <p className="text-[11px] text-gray-400">{tUi(uiLanguage, 'promptLibrarySessionPromptHint')}</p>
            </div>
          )}
          <form onSubmit={handleSend} className="shrink-0 bg-gray-900 rounded-lg p-4 border border-teal-500/30">
            <div className="flex space-x-4">
              {canFeedLlm(authProfile) && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    className="hidden"
                    id="file-upload"
                    disabled={uploadingFile}
                    accept={getFeedFileAccept(authProfile)}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`px-4 py-3 bg-gray-800 hover:bg-gray-700 border border-teal-500/30 rounded-lg text-white font-semibold cursor-pointer transition-all duration-200 flex items-center space-x-2 ${uploadingFile ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={isSuperadminRole(authProfile?.role)
                      ? tUi(uiLanguage, 'appUploadAnyFileTitle')
                      : isOrgAdminRole(authProfile?.role)
                        ? tUi(uiLanguage, 'appUploadAdminFileTitle')
                        : tUi(uiLanguage, 'appUploadOperatorFileTitle')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>
                      {uploadingFile
                        ? 'Uploading...'
                        : (isSuperadminRole(authProfile?.role) ? 'Upload Any File' : 'Upload File')}
                    </span>
                  </label>
                </>
              )}
              <div className="flex-1 relative">
                <p className="text-xs text-gray-500 mb-1">
                  Chat language: {getLanguageLabel(chatLanguage === '__custom__' ? customChatLanguage : chatLanguage)}
                  {' · '}
                  Client deliverables: {getLanguageLabel(reportLanguage === '__custom__' ? customReportLanguage : reportLanguage)}
                  {' · '}
                  <span className="text-gray-600">Enter = newline · Click Send to submit</span>
                </p>
                <textarea
                  key={`chat-draft-${chatDraftEpoch}-${mandatePromptId || 'none'}`}
                  ref={chatInputRef}
                  rows={3}
                  value={effectiveChatDraft}
                  onChange={(e) => handleChatDraftChange(e.target.value)}
                  placeholder={getChatPlaceholder(chatLanguage, customChatLanguage, speechSupported)}
                  className="w-full px-4 py-3 bg-black border border-teal-500/30 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-white placeholder-gray-400 resize-none"
                  disabled={loading}
                />
                {isListening && (
                  <div className="absolute right-3 top-3 flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-red-400">Listening...</span>
                  </div>
                )}
              </div>
              {speechSupported && (
                <button
                  type="button"
                  onClick={toggleConversationMode}
                  disabled={loading}
                  className={`px-4 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2 ${
                    'bg-teal-500 hover:bg-teal-400 text-black'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={tUi(uiLanguage, 'appStartConversationModeTitle')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <span className="text-sm">Conversation</span>
                </button>
              )}
              <button
                type="submit"
                disabled={loading || !effectiveChatDraft.trim()}
                className="px-6 py-3 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-teal-500/50"
              >
                Send
              </button>
            </div>
            
            {/* Voice Controls */}
            <div className="mt-3 flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              {speechSupported ? (
                <span className="text-gray-400 flex items-center space-x-2">
                  <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <span>
                    {conversationMode && isListening 
                      ? 'Conversation mode active - speak naturally' 
                      : 'Voice input available'}
                  </span>
                </span>
              ) : (
                <span className="text-gray-500">Voice input not supported in this browser</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {isSpeaking && (
                <button
                  type="button"
                  onClick={stopSpeaking}
                  className="text-red-400 hover:text-red-300 flex items-center space-x-2 text-xs"
                  title={tUi(uiLanguage, 'appStopSpeakingTitle')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                  <span>Stop</span>
                </button>
              )}
              {elevenLabsAvailable && availableVoices.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowVoiceSelector(!showVoiceSelector)}
                  className="text-teal-400 hover:text-teal-300 flex items-center space-x-2 text-xs"
                  title={tUi(uiLanguage, 'appSelectVoiceTitle')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                  <span>Voice</span>
                </button>
              )}
              <button
                type="button"
                onClick={toggleVoice}
                className={`flex items-center space-x-2 text-xs transition-all ${
                  voiceEnabled
                    ? 'text-teal-400 hover:text-teal-300'
                    : 'text-gray-500 hover:text-gray-400'
                }`}
                title={voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}
              >
                {voiceEnabled ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                    <span>Voice ON</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                    <span>Voice OFF</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Voice Selector Dropdown */}
          {showVoiceSelector && elevenLabsAvailable && availableVoices.length > 0 && (
            <div className="mt-2 bg-gray-800 rounded-lg p-4 border border-teal-500/30 max-h-60 overflow-y-auto">
              <h4 className="text-sm font-semibold text-teal-400 mb-3">Select Voice ({elevenLabsAvailable ? 'ElevenLabs' : 'Web Speech'})</h4>
              <div className="space-y-2">
                {availableVoices.map((voice) => (
                  <button
                    key={voice.voice_id}
                    type="button"
                    onClick={() => {
                      setSelectedVoiceId(voice.voice_id);
                      // Save voice preference to localStorage
                      localStorage.setItem('elevenlabs_voice_id', voice.voice_id);
                      setShowVoiceSelector(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                      selectedVoiceId === voice.voice_id
                        ? 'bg-teal-500/20 border border-teal-500 text-teal-300'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{voice.name}</span>
                      {selectedVoiceId === voice.voice_id && (
                        <svg className="w-4 h-4 text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    {voice.description && (
                      <p className="text-xs text-gray-400 mt-1">{voice.description}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
          </>
          ) : (
            <div className="bg-gray-900 rounded-lg p-4 border border-teal-500/30 text-sm text-gray-400">
              Read-only access: review conversations and export reports from the sidebar. Chat is disabled for your role.
            </div>
          )
        )}
        </div>
      </main>
      )}
      </div>

      {showClientSupport && (
        <ClientSupportForm
          userEmail={authProfile?.user_email || ''}
          userName={authProfile?.user_name || ''}
          onClose={() => setShowClientSupport(false)}
          onSuccess={(message) => {
            setActionFeedback({ message, variant: 'success' });
            setShowClientSupport(false);
          }}
        />
      )}

      {pdfPasswordPrompt && (
        <PdfPasswordPrompt
          uiLanguage={uiLanguage}
          key={`${pdfPasswordPrompt.filename}-${pdfPasswordPrompt.passwordInvalid}`}
          filename={pdfPasswordPrompt.filename}
          passwordInvalid={pdfPasswordPrompt.passwordInvalid}
          queuePosition={pdfPasswordPrompt.queuePosition}
          onSubmit={handlePdfPasswordSubmit}
          onCancel={handlePdfPasswordCancel}
          submitting={uploadingFile}
        />
      )}

      <footer className="app-footer">
        <span className="text-teal-400/90 font-medium">{getProductBrandForMode(consultantMode).productName.toUpperCase()}</span>
        {' · v1.0.0'}
      </footer>
    </div>
  );
}

function App() {
  const [authProfile, setAuthProfile] = useState(() => getStoredAuthProfile());
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authView, setAuthView] = useState('login');
  const [setupToken, setSetupToken] = useState('');
  const [showSplash, setShowSplash] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);

  const clearAuthQuery = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('setup');
    url.searchParams.delete('signup');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get('setup');
    if (setup) {
      setSetupToken(setup);
      setAuthView('setup');
      return;
    }
    if (params.get('signup') === '1') {
      setAuthView('signup');
    }
  }, []);

  useEffect(() => {
    const validateAuth = async () => {
      // Always try Openi SSO bootstrap first so a new handoff replaces any
      // existing localStorage session (same browser, different Openi user).
      try {
        const ssoResponse = await apiFetch('/api/auth/sso/bootstrap', {
          credentials: 'include',
        });
        if (ssoResponse.ok) {
          const data = await parseJsonResponse(ssoResponse);
          if (data?.access_token) {
            storeAuth(data);
            setPendingProfile(getStoredAuthProfile());
            setShowSplash(true);
            setCheckingAuth(false);
            return;
          }
        }
      } catch {
        // No SSO cookie or SSO disabled — continue with normal auth.
      }

      const token = getStoredAuthToken();
      if (!token) {
        setCheckingAuth(false);
        return;
      }

      try {
        const response = await apiFetch('/api/auth/me');
        if (response.ok) {
          const profile = normalizeAuthProfile(await parseJsonResponse(response));
          if (!profile || !profile.tenant_id) {
            clearAuth();
            setAuthProfile(null);
            return;
          }
          setAuthProfile(profile);
          localStorage.setItem(AUTH_PROFILE_KEY, JSON.stringify(profile));
        } else {
          clearAuth();
          setAuthProfile(null);
        }
      } catch {
        clearAuth();
        setAuthProfile(null);
      } finally {
        setCheckingAuth(false);
      }
    };

    validateAuth();
  }, []);

  const handleLoginSuccess = (profile) => {
    setPendingProfile(profile);
    setShowSplash(true);
  };

  const handleSplashComplete = () => {
    setAuthProfile(pendingProfile || getStoredAuthProfile());
    setPendingProfile(null);
    setShowSplash(false);
  };

  const handleLogout = () => {
    clearAuth();
    clearStoredAdminView();
    setAuthProfile(null);
    setShowSplash(false);
    setPendingProfile(null);
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400">Loading…</p>
      </div>
    );
  }

  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  if (!getStoredAuthToken() && !hasLegacyApiKeyAuth()) {
    if (authView === 'setup') {
      return (
        <SetupAccountPage
          setupToken={setupToken}
          onComplete={() => {
            clearAuthQuery();
            setAuthView('login');
            setSetupToken('');
          }}
        />
      );
    }
    if (authView === 'signup') {
      return (
        <SignupRequestPage
          onBackToLogin={() => {
            clearAuthQuery();
            setAuthView('login');
          }}
        />
      );
    }
    return (
      <LoginGate
        onSuccess={handleLoginSuccess}
      />
    );
  }

  return <ConsultantApp authProfile={authProfile} onLogout={handleLogout} />;
}

export default App;
